/**
 * TodoListView - Vista principal estilo ToDoList
 * Muestra contactos en tabla con árbol de listas
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { foldersAPI, todolistAPI, prospectAPI, draftsAPI, bulkAPI, statsAPI, iconsAPI } from '../../services/api'
import FolderTree from './FolderTree'
import ContactsTable from './ContactsTable'
import ProspectPanel from '../prospect/ProspectPanel'
import AddContactModal from './AddContactModal'
import WorkAreaPills from './WorkAreaPills'
import { filterByWorkArea } from '../../utils/dateBusinessDays'
import ColumnSelector from './ColumnSelector'
import IconSelectorModal from '../shared/IconSelectorModal'
import FrozenStatusChip from './FrozenStatusChip'
import { getDefaultWidths, getDefaultVisible } from './columnConfig'
import useCRMStore from '../../store/useCRMStore'
import { useFrozenMerge } from '../../hooks/useFrozenMerge'
import PendingNewPill from './PendingNewPill'
import SortPresetDropdown from './SortPresetDropdown'
import BulkAddToCampaignPicker from '../campaigns/BulkAddToCampaignPicker'
import SelectAllFilteredBanner from './SelectAllFilteredBanner'
import { useICPendingQueue } from '../../hooks/useICPendingQueue'
import ICConfirmationModal from './ICConfirmationModal'
import { useAgentPresence } from '../../hooks/useAgentPresence'
import DayPickerModal from '../shared/DayPickerModal'
import BulkFlagSelectorModal from '../shared/BulkFlagSelectorModal'
import SmartRescheduleModal from './SmartRescheduleModal'

const COLLAPSE_DELAY = 400

function BulkAgentPicker({ onPick, onCancel }) {
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    import('../../services/api').then(({ wassengerAPI }) => {
      wassengerAPI.getTeam().then(team => {
        const EXCLUDED = ['asignables', 'bot wassenger', 'ninguno']
        setAgents(team.filter(a => {
          const n = (a.displayName || '').toLowerCase()
          return !EXCLUDED.some(ex => n.includes(ex))
        }))
      }).finally(() => setLoading(false))
    })
  }, [])
  return (
    <div className="p-4 max-h-96 overflow-y-auto">
      {loading ? (
        <div className="text-center py-4 text-gray-500 text-sm">Cargando equipo...</div>
      ) : (
        <div className="space-y-1">
          <button
            onClick={() => onPick(null, null)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg"
          >
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300 flex-shrink-0" />
            <span className="uppercase">Sin asignar</span>
          </button>
          {agents.map(a => (
            <button
              key={a.id}
              onClick={() => onPick(a.displayName, a.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-surface-dark-hover rounded-lg"
            >
              <span className={'w-2.5 h-2.5 rounded-full flex-shrink-0 ' + (a.color === 'blue' ? 'bg-blue-500' : a.color === 'red' ? 'bg-red-500' : a.color === 'green' ? 'bg-green-500' : a.color === 'orange' ? 'bg-orange-500' : a.color === 'yellow' ? 'bg-yellow-500' : a.color === 'pink' ? 'bg-pink-500' : a.color === 'purple' ? 'bg-purple-500' : 'bg-gray-400')} />
              <span className="uppercase">{a.displayName}</span>
              {a.role === 'admin' && <span className="text-[10px] text-gray-400 ml-auto">Admin</span>}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-surface-dark-border flex justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg">Cancelar</button>
      </div>
    </div>
  )
}

// Contador FIJO: cuadrado redondeado (como el avatar del prospect panel), tamaño constante grande,
// SIEMPRE visible (incluso en 0, sin animación de tamaño). textColor: 'white' (default) | 'dark'.
// "Escribieron hoy": registro persistente (localStorage) de phones YA VISTOS por dia + scope
// (agente/carpeta). Marca como NUEVO lo que entro desde la ultima visita del agente a esa vista.
// Se reinicia solo al cambiar el dia (el bucket wrote_today es diario). 2026-06-23.
const WT_SEEN_KEY = 'wt_seen_v1'
function wtToday() { try { return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' }) } catch { return new Date().toISOString().slice(0, 10) } }
function wtLoadSeen() {
  try { const raw = JSON.parse(localStorage.getItem(WT_SEEN_KEY) || 'null'); if (raw && raw.date === wtToday() && raw.scopes) return raw } catch {}
  return { date: wtToday(), scopes: {} }
}
function wtSaveSeen(obj) { try { localStorage.setItem(WT_SEEN_KEY, JSON.stringify(obj)) } catch {} }
function wtScopeKey(f, userPhone) {
  if (f && f.folder) return f.folder.es_sistema ? `agent:${f.folder.nombre}` : `folder:${f.folder.id}`
  if (f && f.label) return `label:${f.label.id}`
  if (f && f.department) return `dept:${f.department.id}`
  return `self:${userPhone || 'me'}`
}

function FixedCounter({ value, bg, textColor = 'white', label, onClick, active = false, pulse = false }) {
  const v = value || 0
  return (
    <div
      onClick={onClick}
      className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg tabular-nums select-none shadow-sm ${bg} ${textColor === 'dark' ? 'text-gray-900' : 'text-white'}${onClick ? ' cursor-pointer hover:brightness-95 active:scale-95 transition' : ''}${active ? ' ring-2 ring-offset-1 ring-amber-600 dark:ring-offset-surface-dark-alt counter-active-pulse' : ''}${pulse ? ' counter-new-pulse' : ''}`}
      title={onClick ? `${label}: ${v} (click para filtrar)` : `${label}: ${v}`}
    >
      {v}
    </div>
  )
}

// Contador con forma de BOMBILLA ENCENDIDA: el bloque amarillo (mismo cuadrado redondeado que los
// demás contadores) es el vidrio de la bombilla, y debajo se dibuja el soquete (base roscada) en SVG,
// de modo que todo el bloque parece una bombilla encendida. El cuerpo (w-10 h-10) alinea con los otros.
function BulbCounter({ value, label, onClick, active = false, pulse = false }) {
  const v = value || 0
  return (
    <div className="flex flex-col items-center flex-shrink-0 select-none" title={onClick ? `${label}: ${v} (click para filtrar)` : `${label}: ${v}`}>
      <div
        onClick={onClick}
        className={`w-10 h-10 rounded-xl bg-yellow-400 text-white flex items-center justify-center font-bold text-lg tabular-nums shadow-sm${onClick ? ' cursor-pointer hover:brightness-95 active:scale-95 transition' : ''}${active ? ' ring-2 ring-offset-1 ring-amber-600 dark:ring-offset-surface-dark-alt counter-active-pulse' : ''}${pulse ? ' counter-new-pulse' : ''}`}
      >
        {v}
      </div>
      <svg width="26" height="13" viewBox="0 0 26 13" className="-mt-0.5" aria-hidden="true">
        <rect x="5" y="0" width="16" height="3.5" rx="1.4" fill="#9ca3af" />
        <path d="M6.5 3.5 H19.5 L18 8.5 H8 Z" fill="#6b7280" />
        <line x1="8" y1="5.2" x2="18" y2="5.2" stroke="#e5e7eb" strokeWidth="0.9" />
        <line x1="8.6" y1="6.9" x2="17.4" y2="6.9" stroke="#e5e7eb" strokeWidth="0.9" />
        <path d="M10 8.5 H16 L14 12.5 H12 Z" fill="#4b5563" />
      </svg>
    </div>
  )
}

const SORT_STORAGE_PREFIX_V2 = 'crm_todolist_sort_multi_v1_'
const DEFAULT_SORT = []  // vacío = default invisible del backend (created_at desc)
const ALLOWED_SORT_COLUMNS = ['created_at','updated_at','next_followup_date','name','phone','assigned_to','hora','labels','completed_campaigns','last_from_client','bandera','icon_current','no_contesta_dia','hint']
// Contadores de accion del toolbar -> action_type de agent_action_stats (filtro global del dia).
const COUNTER_ACTION_TYPES = { act_si_contesta: 'si_contesta', act_si_whatsapp: 'si_whatsapp', act_no_contesta: 'no_contesta' }
const loadSortFromStorage = (phone) => {
  if (!phone) return DEFAULT_SORT
  try {
    const raw = localStorage.getItem(SORT_STORAGE_PREFIX_V2 + phone)
    if (!raw) return DEFAULT_SORT
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_SORT
    const valid = parsed.filter(s => s && ALLOWED_SORT_COLUMNS.includes(s.column) && (s.direction === 'asc' || s.direction === 'desc'))
    return valid.length > 0 ? valid.slice(0, 2) : DEFAULT_SORT
  } catch { return DEFAULT_SORT }
}
const saveSortToStorage = (phone, sortArr) => {
  if (!phone) return
  try { localStorage.setItem(SORT_STORAGE_PREFIX_V2 + phone, JSON.stringify(sortArr)) } catch {}
}
const isDefaultSortConfig = (s) => Array.isArray(s) && s.length === 0

// Presets de orden "Vista" (dropdown junto a Resetear orden). Persistidos por usuario.
// Un preset es un MODO server-side (sort_preset), excluyente con el orden por columnas:
// al activar uno se limpia sortConfig; al clickear una columna se limpia el preset.
const SORT_PRESET_STORAGE_PREFIX = 'crm_todolist_sort_preset_v1_'
const VALID_SORT_PRESETS = ['lo_mejor', 'lo_continuo']
const loadSortPresetFromStorage = (phone) => {
  if (!phone) return null
  try {
    const raw = localStorage.getItem(SORT_PRESET_STORAGE_PREFIX + phone)
    return VALID_SORT_PRESETS.includes(raw) ? raw : null
  } catch { return null }
}
const saveSortPresetToStorage = (phone, preset) => {
  if (!phone) return
  try {
    if (preset) localStorage.setItem(SORT_PRESET_STORAGE_PREFIX + phone, preset)
    else localStorage.removeItem(SORT_PRESET_STORAGE_PREFIX + phone)
  } catch {}
}

export default function TodoListView() {
  // User from store
  const { user } = useCRMStore()
  const enterFrozen = useCRMStore(s => s.enterFrozen)
  const refreshSnapshot = useCRMStore(s => s.refreshSnapshot)
  const exitFrozen = useCRMStore(s => s.exitFrozen)
  const extendFrozenSnapshot = useCRMStore(s => s.extendFrozenSnapshot)
  const markNewRow = useCRMStore(s => s.markNewRow)
  const resetDeadline = useCRMStore(s => s.resetDeadline)
  const ensureFrozen = useCRMStore(s => s.ensureFrozen)
  const frozenActive = useCRMStore(s => s.frozen.active)
  const fullDayMode = useCRMStore(s => s.frozen.fullDayMode)

  // IC Pending Queue: confirmacion de icono tras guardar nota
  const {
    currentPending,
    currentIndex,
    totalCount,
    loading: icLoading,
    loadPending,
    confirmCurrent,
    enqueueAfterNoteSave,
  } = useICPendingQueue()

  useEffect(() => {
    loadPending()
  }, [loadPending])

  // Frozen-mode merge: aplica fresh server data al snapshot frozen preservando posiciones.
  // Ya no compensa scrollTop preemptivamente (INC-034 2026-05-04): los nuevos
  // detectados van a banner pill o auto-insert solo si scrollTop ~ 0.
  const tableScrollRef = useRef(null)
  const applyServerUpdate = useFrozenMerge()
  // Ref para que el interval de auto-refresh (deps:[]) siempre lea la versión actual
  // del helper. Sin esto, el interval queda capturado con la primera versión y nunca
  // ve cambios de frozen.active/snapshot.
  const applyServerUpdateRef = useRef(applyServerUpdate)
  useEffect(() => { applyServerUpdateRef.current = applyServerUpdate }, [applyServerUpdate])

  // Column visibility & widths (persisted per agent)
  const colWidthKey = `crm_col_widths_${user?.phone || 'default'}`
  const colVisKey = `crm_col_visible_${user?.phone || 'default'}`

  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_col_widths_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : getDefaultWidths()
    } catch { return getDefaultWidths() }
  })

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_col_visible_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : getDefaultVisible()
    } catch { return getDefaultVisible() }
  })

  const handleVisibilityChange = (cols) => {
    setVisibleColumns(cols)
    localStorage.setItem(colVisKey, JSON.stringify(cols))
  }

  const handleWidthChange = (widths) => {
    setColumnWidths(widths)
    localStorage.setItem(colWidthKey, JSON.stringify(widths))
  }

  // Estado
  const [folders, setFolders] = useState([])
  const [contacts, setContacts] = useState([])
  const [selectedFolder, setSelectedFolder] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_filter_folder_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })
  const [selectedContact, setSelectedContact] = useState(null)
  const [dayPickerContact, setDayPickerContact] = useState(null)
  // CoSelect: publicar seleccion al backend cada 30s + release on unmount/unload
  useAgentPresence(selectedContact?.phone || null)
  const [loading, setLoading] = useState(true)
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)

  // Auto-collapse panel izquierdo
  const [isFilterExpanded, setIsFilterExpanded] = useState(false)
  const filterTimerRef = useRef(null)

  // Infinite scroll
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const limit = 50
  // Excepción HOY: el área 'today' carga TODOS los contactos de una (sin scroll infinito),
  // porque nunca es un set muy grande. Cap alto de seguridad; si lo excediera, el scroll
  // infinito sigue cubriendo el resto (no se pierde nada).
  const TODAY_AREA_LIMIT = 1000
  const currentOffsetRef = useRef(0)

  // Selección múltiple y acciones masivas
  const [selectedIds, setSelectedIds] = useState([])
  // Gmail-style "select all filtered": cuando es true, selectedIds contiene IDs
  // virtuales que cubren TODO el filtro actual (no solo los visibles paginados).
  // totalFiltered se sincroniza con response.total del backend en cada fetch.
  const [selectAllFiltered, setSelectAllFiltered] = useState(false)
  const [totalFiltered, setTotalFiltered] = useState(0)
  const [showBulkDateModal, setShowBulkDateModal] = useState(false)
  const [showBulkFolderModal, setShowBulkFolderModal] = useState(false)
  const [showBulkIconModal, setShowBulkIconModal] = useState(false)
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [bulkDate, setBulkDate] = useState('')
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)
  const [showBulkFlagModal, setShowBulkFlagModal] = useState(false)
  const [showSmartReschedule, setShowSmartReschedule] = useState(false)
  const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false)
  const [applyingBulkAction, setApplyingBulkAction] = useState(false)

  // Draft phones (for yellow row highlight)
  const [draftPhones, setDraftPhones] = useState(new Set())

  // Daily stats for dynamic counters
  const [dailyStats, setDailyStats] = useState({ si_contesta: 0, si_whatsapp: 0, no_contesta: 0, drafts_pending: 0 })
  // Conteo IC ≥ "Diamante Info+" (calidad igual o mejor), scopeado por área de trabajo activa
  // (o global si no hay área). Fuente: /icons/counts → ge_diamond_plus.
  const [icCount, setIcCount] = useState(0)
  // Conteo de bombillas encendidas (bulb_on) creadas hace ≤2 días, mismo scope que el IC.
  const [bulbCount, setBulbCount] = useState(0)
  const [wroteTodayCount, setWroteTodayCount] = useState(0)
  const wroteTodaySeenRef = useRef(null)   // E: conteo visto (ack); null=sin inicializar
  const wroteTodayCountRef = useRef(0)
  const [hasNewWriters, setHasNewWriters] = useState(false)
  // Escribieron hoy: phones marcados NUEVOS desde la ultima visita (chip + agrupados arriba).
  const [newWrotePhones, setNewWrotePhones] = useState(() => new Set())
  // Vista bajo la que se cargo contacts (reconciliar NUEVOS solo cuando la grilla ES wrote_today).
  const [contactsView, setContactsView] = useState(null)
  const prevCounterFilterRef = useRef(null)
  // Ref al refrescador de contadores (debounced) para llamarlo desde loadContacts sin meterlo en sus deps.
  const refreshCountersRef = useRef(null)
  const counterDebounceRef = useRef(null)

  // Filtros de Wassenger
  const [labels, setLabels] = useState([])
  const [departments, setDepartments] = useState([])
  const [selectedLabel, setSelectedLabel] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_filter_label_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })
  const [selectedDepartment, setSelectedDepartment] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_filter_dept_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })

  const [iconFilter, setIconFilter] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_filter_icons_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })

  const [flagFilter, setFlagFilter] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_filter_flags_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })

  // Filtro de borradores pendientes (click en el indicador "notas pendientes de grabar").
  // Transient (NO persiste, como el drilldown): vista global de "todos mis drafts sin guardar".
  const [draftFilter, setDraftFilter] = useState(false)

  // Filtro de contador del toolbar (radio: un solo contador-filtro activo a la vez).
  // Transient (NO persiste). Mutuamente excluyente con draftFilter. Valores:
  // 'ic_ge_diamond' | 'bulb_recent' | 'act_si_contesta' | 'act_si_whatsapp' | 'act_no_contesta'.
  const [counterFilter, setCounterFilter] = useState(null)

  // Búsqueda
  const [searchTerm, setSearchTerm] = useState('')

  // Receive focus request from notification modal: set search to that phone
  const focusedContactPhone = useCRMStore(s => s.focusedContactPhone)
  const setFocusedContactPhone = useCRMStore(s => s.setFocusedContactPhone)
  // Al llegar por drilldown/notificacion recordamos el telefono objetivo para auto-seleccionarlo
  // cuando cargue la grilla (aunque ya hubiera un contacto seleccionado antes -> evita panel desfasado).
  const pendingSelectPhoneRef = useRef(null)
  useEffect(() => {
    if (focusedContactPhone) {
      pendingSelectPhoneRef.current = focusedContactPhone
      setSearchTerm(focusedContactPhone)
      // Clear after a short delay so next time it triggers again
      const t = setTimeout(() => setFocusedContactPhone(null), 500)
      // Safety: no dejar el ref colgado si el contacto nunca aparece en la grilla
      const t2 = setTimeout(() => { pendingSelectPhoneRef.current = null }, 4000)
      return () => { clearTimeout(t); clearTimeout(t2) }
    }
  }, [focusedContactPhone, setFocusedContactPhone])

  // Drill-down desde Analiticas (ScheduledHeatmapTab): aplica agent + next_followup_date como filtros directos.
  // FIX race condition: lectura snapshot del store al primer render via lazy init de useState.
  // Sin esto, el primer loadContacts corria con drilldownAgent/Date=null y applyServerUpdate
  // mergeaba los 5172 contactos sin filtro con los N filtrados del segundo fetch (bug visible 2026-05-25).
  const pendingDrilldown = useCRMStore(s => s.pendingDrilldown)
  const clearPendingDrilldown = useCRMStore(s => s.clearPendingDrilldown)
  const initialPendingDrilldown = (() => {
    try {
      const pd = useCRMStore.getState().pendingDrilldown
      if (pd?.view === 'todolist') return pd
    } catch { /* ignore */ }
    return null
  })()
  const [drilldownAgent, setDrilldownAgent] = useState(initialPendingDrilldown?.agent || null)
  const [drilldownDate, setDrilldownDate] = useState(initialPendingDrilldown?.next_followup_date || null)
  useEffect(() => {
    if (pendingDrilldown?.view === 'todolist') {
      if (pendingDrilldown.workArea !== undefined) setActiveWorkArea(pendingDrilldown.workArea)
      if (pendingDrilldown.agent !== undefined) setDrilldownAgent(pendingDrilldown.agent || null)
      if (pendingDrilldown.next_followup_date !== undefined) setDrilldownDate(pendingDrilldown.next_followup_date || null)
      clearPendingDrilldown()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDrilldown?.view])

  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Work area filter (lazy init considera drilldown.workArea si viene seteado al primer mount)
  const [activeWorkArea, setActiveWorkArea] = useState(() => {
    if (initialPendingDrilldown && initialPendingDrilldown.workArea !== undefined) {
      return initialPendingDrilldown.workArea  // puede ser null (drilldown desde heatmap)
    }
    try {
      const saved = localStorage.getItem(`crm_work_area_${useCRMStore.getState().user?.phone || 'default'}`)
      return saved || null
    } catch { return null }
  })
  const [workAreaCounts, setWorkAreaCounts] = useState({ overdue: 0, yesterday: 0, today: 0, tomorrow: 0, future: 0, all_mine: 0, activos: 0 })

  // Helper: si fullDayMode esta activo, NO descongelar al cambiar area/filtros.
  // En modo dia el orden se mantiene hasta las 00:00 Chile aunque el agente cambie
  // de scope (refreshSnapshot dentro de loadContacts ajusta el snapshot al nuevo set).
  // Cambios de orden por columna SI descongelan (ver handleSortChange).
  const exitFrozenIfNotFullDay = useCallback(() => {
    if (useCRMStore.getState().frozen.fullDayMode) return
    exitFrozen()
  }, [exitFrozen])

  // Persistent filter setters (save to localStorage per agent)
  const setActiveWorkAreaPersist = useCallback((val) => {
    setActiveWorkArea(val)
    // C (2026-06-23): tocar un area de trabajo apaga el filtro de contador/borradores activo
    // (Ivan iba directo a HOY y no cargaba porque el contador seguia enmascarando la grilla).
    setCounterFilter(null)
    setDraftFilter(false)
    // Clear prospect selection + collapse panel + pins + search:
    // Cualquier cambio de scope limpia el estado contextual previo del agente.
    setSelectedContact(null)
    setIsPanelCollapsed(true)
    exitFrozenIfNotFullDay()
    setSearchTerm('')
    try {
      const key = `crm_work_area_${user?.phone || 'default'}`
      if (val) localStorage.setItem(key, val)
      else localStorage.removeItem(key)
    } catch {}
  }, [user?.phone, exitFrozenIfNotFullDay])

  const setSelectedLabelPersist = useCallback((val) => {
    setSelectedLabel(val)
    exitFrozenIfNotFullDay()
    try {
      const key = `crm_filter_label_${user?.phone || 'default'}`
      if (val) localStorage.setItem(key, JSON.stringify(val))
      else localStorage.removeItem(key)
    } catch {}
  }, [user?.phone, exitFrozenIfNotFullDay])

  const setSelectedDepartmentPersist = useCallback((val) => {
    setSelectedDepartment(val)
    exitFrozenIfNotFullDay()
    try {
      const key = `crm_filter_dept_${user?.phone || 'default'}`
      if (val) localStorage.setItem(key, JSON.stringify(val))
      else localStorage.removeItem(key)
    } catch {}
  }, [user?.phone, exitFrozenIfNotFullDay])

  const setSelectedFolderPersist = useCallback((val) => {
    setSelectedFolder(val)
    exitFrozenIfNotFullDay()
    try {
      const key = `crm_filter_folder_${user?.phone || 'default'}`
      if (val) localStorage.setItem(key, JSON.stringify(val))
      else localStorage.removeItem(key)
    } catch {}
  }, [user?.phone, exitFrozenIfNotFullDay])

  // Auto-refresh: refs para evitar dependencias problemáticas
  const contactsRef = useRef([])
  const setIconFilterPersist = useCallback((val) => {
    setIconFilter(val)
    try {
      const key = `crm_filter_icons_${user?.phone || 'default'}`
      if (val === null || val === undefined) localStorage.removeItem(key)
      else localStorage.setItem(key, JSON.stringify(val))
    } catch {}
  }, [user])

  const setFlagFilterPersist = useCallback((val) => {
    setFlagFilter(val)
    try {
      const key = `crm_filter_flags_${user?.phone || 'default'}`
      if (val === null || val === undefined) localStorage.removeItem(key)
      else localStorage.setItem(key, JSON.stringify(val))
    } catch {}
  }, [user])

  // Toggle del filtro de borradores pendientes (click en el indicador amarillo).
  // Activa una vista GLOBAL de los drafts del agente (loadContacts NO manda work_area/folder
  // en modo draft) y descongela. Limpia búsqueda y selección para una vista limpia. El Reset lo apaga.
  const handleToggleDraftFilter = useCallback(() => {
    const next = !draftFilter
    setDraftFilter(next)
    if (next) {
      setCounterFilter(null)  // mutua exclusion con los contadores-filtro
      setSearchTerm('')
      setSelectedContact(null)
      setIsPanelCollapsed(true)
    }
    exitFrozenIfNotFullDay()
  }, [draftFilter, exitFrozenIfNotFullDay])

  // Toggle de un contador-filtro del toolbar (radio: re-click apaga; click en otro cambia).
  // Mutuamente excluyente con draftFilter. Igual que drafts: limpia busqueda/seleccion y descongela.
  const handleToggleCounterFilter = useCallback((id) => {
    const next = counterFilter === id ? null : id
    setCounterFilter(next)
    if (next) {
      setDraftFilter(false)
      setSearchTerm('')
      setSelectedContact(null)
      setIsPanelCollapsed(true)
    }
    // E: al abrir/ver el bucket fucsia, marcar como visto (apaga el pulso de nuevo escritor).
    if (id === 'wrote_today') { wroteTodaySeenRef.current = wroteTodayCountRef.current; setHasNewWriters(false) }
    exitFrozenIfNotFullDay()
  }, [counterFilter, exitFrozenIfNotFullDay])

  const filtersRef = useRef({ folder: null, label: null, department: null, search: '', iconFilter: null, flagFilter: null })
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT)
  const sortRef = useRef(DEFAULT_SORT)
  // Preset de orden activo (null = Normal/columnas). Excluyente con sortConfig.
  const [sortPreset, setSortPreset] = useState(null)
  const sortPresetRef = useRef(null)
  const sortLoadedRef = useRef(false)
  // Spinner discreto en header del sort activo durante el fetch post-click.
  // Comunica al agente que se esta cargando el orden por backend (las urgentes
  // u otros bucket pueden estar en otras paginas y aparecen post-fetch).
  const [sortLoadingColumn, setSortLoadingColumn] = useState(null)

  // Cargar sort persistido por usuario (Zustand rehydrate es async)
  useEffect(() => {
    if (user?.phone && !sortLoadedRef.current) {
      sortLoadedRef.current = true
      const loadedPreset = loadSortPresetFromStorage(user.phone)
      sortPresetRef.current = loadedPreset
      setSortPreset(loadedPreset)
      // Si hay preset persistido, el orden por columnas no aplica (invariante XOR).
      const loaded = loadedPreset ? DEFAULT_SORT : loadSortFromStorage(user.phone)
      sortRef.current = loaded
      setSortConfig(loaded)
    }
  }, [user?.phone])

  // Mouse enter solo cancela el cierre pendiente (no auto-expande, requiere click).
  const handleFilterEnter = useCallback(() => {
    if (filterTimerRef.current) {
      clearTimeout(filterTimerRef.current)
      filterTimerRef.current = null
    }
  }, [])

  const handleFilterLeave = useCallback(() => {
    filterTimerRef.current = setTimeout(() => {
      setIsFilterExpanded(false)
      filterTimerRef.current = null
    }, COLLAPSE_DELAY)
  }, [])

  // Click expande el panel (no toggle: cierre solo via mouseleave)
  const handleFilterClick = useCallback(() => {
    if (filterTimerRef.current) {
      clearTimeout(filterTimerRef.current)
      filterTimerRef.current = null
    }
    setIsFilterExpanded(true)
  }, [])

  useEffect(() => {
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current)
    }
  }, [])

  // Fetch daily stats para los contadores dinámicos. Extraído a useCallback para poder
  // refrescar EN VIVO (no solo cada 30s) tras crear/cancelar drafts o guardar notas.
  const fetchStats = useCallback(async () => {
    try {
      const filterAgent = (selectedFolder && selectedFolder.es_sistema) ? selectedFolder.nombre : null
      const stats = await statsAPI.getDaily(filterAgent)
      setDailyStats(stats)
    } catch {}
  }, [selectedFolder])

  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, 30000)
    return () => clearInterval(interval)
  }, [fetchStats])

  // Conteo IC ≥ Diamante Info+ por área de trabajo activa (mismo scope que el IconFilterModal:
  // work_area + folder/agent/label/department/flag). NO depende de search/draft/icon_filter:
  // es "cuántos prospectos de calidad tengo en esta área". Refresca al cambiar el scope y cada 30s.
  const fetchIcCount = useCallback(async () => {
    try {
      const counts = await iconsAPI.getCounts({
        work_area: activeWorkArea || undefined,
        folder_id: (selectedFolder && !selectedFolder.es_sistema) ? selectedFolder.id : undefined,
        agent: (selectedFolder && selectedFolder.es_sistema) ? selectedFolder.nombre : undefined,
        label: selectedLabel?.id,
        department: selectedDepartment?.id,
        flag_filter: flagFilter || undefined,
      })
      setIcCount(counts?.ge_diamond_plus || 0)
      setBulbCount(counts?.bulb_on_recent || 0)
      const _wt = counts?.wrote_today || 0
      setWroteTodayCount(_wt)
      wroteTodayCountRef.current = _wt
      if (wroteTodaySeenRef.current === null) wroteTodaySeenRef.current = _wt // 1ra carga: lo actual ya esta visto
      else setHasNewWriters(_wt > wroteTodaySeenRef.current)
    } catch { /* silencioso: contador decorativo */ }
  }, [activeWorkArea, selectedFolder, selectedLabel, selectedDepartment, flagFilter])

  useEffect(() => {
    fetchIcCount()
    const interval = setInterval(fetchIcCount, 30000)
    return () => clearInterval(interval)
  }, [fetchIcCount])

  // Refresco EN VIVO de TODOS los contadores (daily + IC + bombilla), debounced 400ms para no spamear
  // en ediciones rápidas. Se llama desde handleProspectUpdate (cambio IC/AS/flag/nota) y desde
  // loadContacts (bulk, día, área, reset) para que reflejen los cambios sin recargar el CRM.
  const refreshCounters = useCallback(() => {
    if (counterDebounceRef.current) clearTimeout(counterDebounceRef.current)
    counterDebounceRef.current = setTimeout(() => { fetchStats(); fetchIcCount() }, 400)
  }, [fetchStats, fetchIcCount])
  refreshCountersRef.current = refreshCounters
  useEffect(() => () => { if (counterDebounceRef.current) clearTimeout(counterDebounceRef.current) }, [])

  // ── Escribieron hoy: reconciliar NUEVOS (vs vistos) + commit al salir. Persistente por dia+scope.
  const wtCommitPhones = useCallback((phones) => {
    const key = wtScopeKey(filtersRef.current, useCRMStore.getState().user?.phone)
    const store = wtLoadSeen()
    const merged = new Set(store.scopes[key] || [])
    phones.forEach(p => merged.add(p))
    store.scopes[key] = [...merged]
    wtSaveSeen(store)
  }, [])
  const reconcileWroteNew = useCallback((list) => {
    const key = wtScopeKey(filtersRef.current, useCRMStore.getState().user?.phone)
    const store = wtLoadSeen()
    const current = list.map(c => c.phone)
    const prev = store.scopes[key]
    if (!prev) {
      store.scopes[key] = current
      wtSaveSeen(store)
      setNewWrotePhones(new Set())
      return
    }
    const seen = new Set(prev)
    setNewWrotePhones(new Set(current.filter(p => !seen.has(p))))
  }, [])
  // Recalcular NUEVOS cuando la grilla muestra wrote_today (carga inicial + silentRefresh en vivo).
  useEffect(() => {
    if (contactsView === 'wrote_today') reconcileWroteNew(contacts)
  }, [contacts, contactsView, reconcileWroteNew])
  // Al SALIR de la vista (toggle off / cambio de area / otro contador): marcar lo mostrado como visto.
  useEffect(() => {
    const prev = prevCounterFilterRef.current
    prevCounterFilterRef.current = counterFilter
    if (prev === 'wrote_today' && counterFilter !== 'wrote_today') {
      wtCommitPhones(contactsRef.current.map(c => c.phone))
      setNewWrotePhones(new Set())
    }
  }, [counterFilter, wtCommitPhones])

  // Manitos en vivo (2026-06-10): poll ligero del set global de manitos levantadas.
  // A diferencia de silentRefresh (que se salta folder/label/department), este poll corre
  // SIEMPRE y solo parchea last_from_client/has_urgent_keyword/has_fondo_keyword in-place en las filas ya
  // cargadas, sin tocar el orden (frozen-safe) ni los filtros activos.
  const handsLoweredAtRef = useRef(new Map())
  const fetchHands = useCallback(async () => {
    try {
      const res = await todolistAPI.getHands()
      const handsMap = new Map((res.hands || []).map(h => [h.phone, h]))
      const now = Date.now()
      setContacts(prev => {
        let changed = false
        const next = prev.map(c => {
          const on = handsMap.has(c.phone)
          const hand = on ? handsMap.get(c.phone) : null
          const urgent = on && !!hand.urgent
          const fondo = on && !!hand.fondo
          // Guard anti-parpadeo: no re-levantar una manito bajada optimísticamente hace
          // <20s (la gestión del agente puede no estar reflejada aún en el poll en vuelo).
          if (on && !c.last_from_client) {
            const loweredAt = handsLoweredAtRef.current.get(c.phone)
            if (loweredAt && now - loweredAt < 20000) return c
          }
          if (!!c.last_from_client === on && !!c.has_urgent_keyword === urgent && !!c.has_fondo_keyword === fondo) return c
          changed = true
          return { ...c, last_from_client: on, has_urgent_keyword: urgent, has_fondo_keyword: fondo }
        })
        return changed ? next : prev
      })
    } catch {}
  }, [])

  useEffect(() => {
    const interval = setInterval(fetchHands, 10000)
    return () => clearInterval(interval)
  }, [fetchHands])

  // Auto-select first contact ONLY on initial load (not on every reload).
  // Without the ref, clearing selection on area change would re-select first
  // contact on the next contacts refresh, defeating the clear.
  const initialAutoSelectDoneRef = useRef(false)
  useEffect(() => {
    if (contacts.length > 0 && !selectedContact && !initialAutoSelectDoneRef.current) {
      initialAutoSelectDoneRef.current = true
      setSelectedContact(contacts[0])
      setIsPanelCollapsed(false)
    }
  }, [contacts.length])

  // Auto-seleccionar el contacto objetivo de un drilldown/notificacion cuando aparece en la grilla,
  // sobrescribiendo cualquier seleccion previa para que el panel no muestre el prospecto anterior.
  useEffect(() => {
    const target = pendingSelectPhoneRef.current
    if (!target || contacts.length === 0) return
    const norm = (p) => String(p || '').replace(/\D/g, '')
    const match = contacts.find(c => norm(c.phone) === norm(target))
    if (match) {
      setSelectedContact(match)
      setIsPanelCollapsed(false)
      pendingSelectPhoneRef.current = null
    }
  }, [contacts])

  // Mantener refs sincronizados
  useEffect(() => {
    contactsRef.current = contacts
  }, [contacts])

  useEffect(() => {
    filtersRef.current = {
      folder: selectedFolder,
      label: selectedLabel,
      department: selectedDepartment,
      search: debouncedSearch
    , workArea: activeWorkArea, iconFilter, flagFilter, drilldownAgent, drilldownDate, draftFilter, counterFilter}
  }, [selectedFolder, selectedLabel, selectedDepartment, debouncedSearch, activeWorkArea, iconFilter, flagFilter, drilldownAgent, drilldownDate, draftFilter, counterFilter])

  // Cargar listas, labels y departments al montar
  useEffect(() => {
    loadFolders()
    loadFilters()
  }, [])

  // Debounce para búsqueda (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Cargar contactos cuando cambian los filtros o la búsqueda
  useEffect(() => {
    loadContacts()
  }, [selectedFolder, selectedLabel, selectedDepartment, debouncedSearch, activeWorkArea, iconFilter, flagFilter, drilldownAgent, drilldownDate, draftFilter, counterFilter])

  // Reset selección masiva cuando cambian filtros: cualquier cambio de scope
  // invalida el conjunto seleccionado (visibles distintos, "select all filtered"
  // ya no aplica al nuevo filtro). Es el patrón Gmail-style: cambias de label,
  // se limpia el "Seleccionar X resultados".
  useEffect(() => {
    setSelectedIds([])
    setSelectAllFiltered(false)
  }, [selectedFolder, selectedLabel, selectedDepartment, debouncedSearch, activeWorkArea, iconFilter, flagFilter, drilldownAgent, drilldownDate, draftFilter, counterFilter])

  // Fetch draft phones on mount and when contacts change (for yellow rows)
  const refreshDraftPhones = useCallback(() => {
    draftsAPI.listDraftPhones().then(phones => {
      setDraftPhones(new Set(Array.isArray(phones) ? phones : []))
    }).catch(() => {})
    // Refresco EN VIVO de los 3 contadores (drafts/si/no contesta): onDraftChanged es el
    // único chokepoint que disparan crear/cancelar draft y guardar nota (cada save hace
    // deleteDraft().finally(onDraftChanged)), así que acá cubrimos los 3 indicadores sin
    // hilar props extra por ProspectPanel/NotesSection.
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    refreshDraftPhones()
  }, [contacts.length, refreshDraftPhones])


  // Fetch work area counts on mount, every 30s, and when agent filter changes
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const filterAgent = (selectedFolder && selectedFolder.es_sistema) ? selectedFolder.nombre : null
        const res = await todolistAPI.getWorkAreaCounts(filterAgent)
        if (res) setWorkAreaCounts(res)
      } catch (e) {
        console.error("Error fetching work area counts:", e)
      }
    }
    fetchCounts()
    const interval = setInterval(fetchCounts, 30000)
    return () => clearInterval(interval)
  }, [selectedFolder])
  // Threshold para considerar al agente "al top de la grilla" (subpixel-tolerant).
  const SCROLL_TOP_THRESHOLD = 5

  // Silent refresh: re-fetch con sort/filtros actuales + applyServerUpdate
  // (preserva orden frozen + detecta nuevos). Reusable desde:
  //   - Auto-refresh setInterval (cada 10s) — sin opciones (default).
  //   - handleProspectUpdate._forceRefresh tras agregar nota / acciones que
  //     mutan campos no cubiertos por optimistic — pasa { force: true }.
  //
  // Decision tree para nuevos detectados (frozen activo):
  //   - scrollTop < SCROLL_TOP_THRESHOLD: auto-insert al top + markNewRow.
  //   - scrollTop >= SCROLL_TOP_THRESHOLD: addPendingNewPhones (banner).
  //
  // Default skip si filters folder/label/department activos. Con `force: true`
  // se ignora ese skip (necesario para refrescar tras una mutacion del agente).
  //
  // Reemplaza el branch isDateDescSort + getNewContacts + useScrollAnchor del
  // flujo anterior, que provocaba drift acumulativo del scroll (INC-034 2026-05-04).
  const silentRefresh = useCallback(async ({ force = false } = {}) => {
    const filters = filtersRef.current
    // Los contadores "globales por agente" (fucsia wrote_today + accion Si/SiWSP/No) SI se
    // auto-refrescan aunque haya carpeta/label/depto: sus ramas (abajo) ya pasan el scope del
    // agente igual que loadContacts, asi que mergear nuevos NO reintroduce la fuga INC-059.
    const _isGlobalCounter = filters.counterFilter === 'wrote_today' || !!COUNTER_ACTION_TYPES[filters.counterFilter]
    if (!force && !_isGlobalCounter && (filters.folder || filters.label || filters.department)) return

    try {
      const currentLen = Math.max(50, contactsRef.current.length)
      const apiFilters = {
        limit: currentLen,
        sort: sortRef.current.map(s => `${s.column}:${s.direction}`).join(','),
      }
      if (sortPresetRef.current) apiFilters.sort_preset = sortPresetRef.current
      if (filters.draftFilter) {
        // Modo borradores: vista GLOBAL de drafts del agente (igual que loadContacts).
        apiFilters.draft_filter = 'has_drafts'
      } else if (filters.counterFilter && COUNTER_ACTION_TYPES[filters.counterFilter]) {
        // Contador de accion: vista GLOBAL del dia por agente (igual que loadContacts).
        apiFilters.action_filter = COUNTER_ACTION_TYPES[filters.counterFilter]
        if (filters.folder && filters.folder.es_sistema) apiFilters.action_agent = filters.folder.nombre
      } else if (filters.counterFilter === 'wrote_today') {
        apiFilters.wrote_today = '1'
        if (filters.folder) { if (filters.folder.es_sistema) apiFilters.agent = filters.folder.nombre; else apiFilters.folder_id = filters.folder.id }
        if (filters.label) apiFilters.label = filters.label.id
        if (filters.department) apiFilters.department = filters.department.id
      } else {
        if (filters.workArea) apiFilters.work_area = filters.workArea
        // Scope cross-vista (fix leak 2026-06-23): MISMO scope que loadContacts. Sin esto,
        // silentRefresh (unico path que MERGEA) corria sin agente/carpeta y el backend caia al
        // auto-scope por req.user.name (el logueado, no el agente de la vista), inyectando o
        // reteniendo filas ajenas (ej: supervisor mirando el HOY de otro agente).
        if (filters.folder) {
          if (filters.folder.es_sistema) apiFilters.agent = filters.folder.nombre
          else apiFilters.folder_id = filters.folder.id
        }
        if (filters.label) apiFilters.label = filters.label.id
        if (filters.department) apiFilters.department = filters.department.id
        // Contadores de icono/area: re-aplicar en el poll para no clobbear el aislamiento.
        if (filters.counterFilter === 'ic_ge_diamond') apiFilters.ic_quality = 'ge_diamond_plus'
        if (filters.counterFilter === 'bulb_recent') apiFilters.bulb_recent = '1'
        if (filters.iconFilter !== null && filters.iconFilter !== undefined) {
          apiFilters.icon_filter = typeof filters.iconFilter === 'string'
            ? filters.iconFilter
            : (Array.isArray(filters.iconFilter) ? filters.iconFilter.join(',') : String(filters.iconFilter))
        }
        if (filters.flagFilter !== null && filters.flagFilter !== undefined) {
          apiFilters.flag_filter = typeof filters.flagFilter === 'string'
            ? filters.flagFilter
            : (Array.isArray(filters.flagFilter) ? filters.flagFilter.join(',') : String(filters.flagFilter))
        }
        if (filters.search) apiFilters.search = filters.search
        // Drill-down filters (set por ScheduledHeatmapTab)
        if (filters.drilldownAgent) apiFilters.agent = filters.drilldownAgent
        if (filters.drilldownDate) apiFilters.next_followup_date = filters.drilldownDate
      }
      // Pasar include_worked_today=1 si frozen está activo OR fullDayMode está
      // activado (opt-in persistido). El segundo caso cubre la primera carga
      // antes de que enterFrozen haga active=true; sin esto, la primera carga de
      // HOY traería un set distinto al que ven los siguientes refreshes.
      const _fzn = useCRMStore.getState().frozen
      if (_fzn.fullDayMode && !filters.search) {
        apiFilters.include_worked_today = 1
      }

      const data = await todolistAPI.getContacts(apiFilters)
      const newContacts = data.contacts || data
      const hashContact = (c) =>
        `${c.id}:${c.icon_current || ''}:${c.updated_at || ''}:${c.next_followup_date || ''}:${c.assigned_to || ''}:${c.bandera || ''}:${c.last_from_client ? '1' : '0'}:${c.has_urgent_keyword ? '1' : '0'}:${c.has_fondo_keyword ? '1' : '0'}:${c.no_contesta_dia ?? ''}:${c.hint || ''}:${(c.watching_agents||[]).join(',')}`
      const oldHash = contactsRef.current.map(hashContact).join('|')
      const newHash = newContacts.map(hashContact).join('|')
      if (oldHash === newHash) return

      const { contacts: mergedContacts, newPhones } = applyServerUpdateRef.current(
        contactsRef.current,
        newContacts,
        useCRMStore.getState().user?.name || ''
      )

      if (newPhones.length === 0) {
        setContacts(mergedContacts)
        setTotal(data.total || newContacts.length)
        setTotalFiltered(data.total ?? newContacts.length)
        return
      }

      const scrollTop = tableScrollRef.current?.scrollTop ?? 0
      if (scrollTop < SCROLL_TOP_THRESHOLD) {
        const freshByPhone = new Map(newContacts.map(c => [c.phone, c]))
        const newContactObjs = newPhones
          .map(p => freshByPhone.get(p))
          .filter(Boolean)
        newPhones.forEach(p => useCRMStore.getState().markNewRow(p))
        setContacts([...newContactObjs, ...mergedContacts])
        setTotal(data.total || newContacts.length)
        setTotalFiltered(data.total ?? newContacts.length)
      } else {
        useCRMStore.getState().addPendingNewPhones(newPhones)
        setContacts(mergedContacts)
        setTotal(data.total || newContacts.length)
        setTotalFiltered(data.total ?? newContacts.length)
      }
    } catch (e) {
      // silent: errores de red transitorios no deben crashear el polling
    }
  }, [])

  // Auto-refresh: cada 10 segundos invocar silentRefresh.
  useEffect(() => {
    const interval = setInterval(silentRefresh, 10000)
    return () => clearInterval(interval)
  }, [silentRefresh])

  // Reactivar frozen mode al activar fullDayMode con grilla descongelada.
  // Caso de uso: agente desactiva "Hasta 00:00", pasan >3 min, frozen expira y
  // descongela. Si reactiva el ticket, espera que el orden vuelva a congelarse
  // desde ese momento. Sin esto, el ticket solo activaría el flag de
  // include_worked_today pero no congelaría el orden hasta el próximo cambio
  // de área/recarga.
  const prevFullDayModeRef = useRef(fullDayMode)
  useEffect(() => {
    const prev = prevFullDayModeRef.current
    prevFullDayModeRef.current = fullDayMode
    if (prev === false && fullDayMode === true && !frozenActive && contactsRef.current.length > 0) {
      ensureFrozen(contactsRef.current)
    }
  }, [fullDayMode, frozenActive, ensureFrozen])

  const loadFolders = async () => {
    try {
      const data = await foldersAPI.getFolders()
      setFolders(data)
    } catch (error) {
      console.error('Error cargando listas:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadFilters = async () => {
    try {
      const [labelsData, departmentsData] = await Promise.all([
        todolistAPI.getLabels(),
        todolistAPI.getDepartments()
      ])
      setLabels(labelsData)
      setDepartments(departmentsData)
    } catch (error) {
      console.error('Error cargando filtros:', error)
    }
  }

  const loadContacts = useCallback(async (append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        // Solo mostrar spinner en carga inicial (grid vacio). En refresh post-save
        // u otros _forceRefresh, mantener el grid montado para evitar parpadeo.
        // setContacts replazara las filas en sitio y React reconciliara solo las que cambiaron.
        if (contactsRef.current.length === 0) {
          setLoadingContacts(true)
        }
        currentOffsetRef.current = 0
      }

      // Excepción HOY: traer todos los contactos de una (sin paginar). Solo aplica cuando
      // realmente estamos en el área 'today' (no en search ni en modo borradores, que
      // ignoran work_area). Si excediera el cap, hasMore quedaría true y el scroll infinito
      // sigue funcionando como en el resto de las áreas.
      const counterActionType = counterFilter ? COUNTER_ACTION_TYPES[counterFilter] : null
      const isTodayArea = !debouncedSearch && !draftFilter && !counterFilter && activeWorkArea === 'today'
      const filters = {
        limit: isTodayArea ? TODAY_AREA_LIMIT : limit,
        offset: append ? currentOffsetRef.current : 0
      }

      // Búsqueda tiene prioridad sobre filtros: si hay search, ignoramos work_area/folder/label/dept/icon/flag
      // así el agente siempre encuentra el prospecto que busca aunque no esté en su scope actual.
      if (debouncedSearch) {
        filters.search = debouncedSearch
      } else if (draftFilter) {
        // Modo borradores pendientes: vista GLOBAL de los drafts del agente.
        // NO mandamos work_area/folder/label/IC/flag → cuadra con el contador drafts_pending.
        filters.draft_filter = 'has_drafts'
      } else if (counterActionType) {
        // Contador de accion (Si/Si WSP/No): vista GLOBAL del dia por agente (como drafts),
        // contactos unicos. El agente se pasa igual que fetchStats (system folder -> su nombre).
        filters.action_filter = counterActionType
        if (selectedFolder && selectedFolder.es_sistema) filters.action_agent = selectedFolder.nombre
      } else if (counterFilter === 'wrote_today') {
        // Vista propia 'Escribieron hoy' (no agendados hoy): scope del agente SIN work_area (como drafts).
        filters.wrote_today = '1'
        if (selectedFolder) { if (selectedFolder.es_sistema) filters.agent = selectedFolder.nombre; else filters.folder_id = selectedFolder.id }
        if (selectedLabel) filters.label = selectedLabel.id
        if (selectedDepartment) filters.department = selectedDepartment.id
      } else {
        if (selectedFolder) {
          if (selectedFolder.es_sistema) {
            filters.agent = selectedFolder.nombre
          } else {
            filters.folder_id = selectedFolder.id
          }
        }
        if (selectedLabel) {
          filters.label = selectedLabel.id
        }
        if (selectedDepartment) {
          filters.department = selectedDepartment.id
        }
        if (iconFilter !== null && iconFilter !== undefined) {
          filters.icon_filter = typeof iconFilter === 'string' ? iconFilter : (Array.isArray(iconFilter) ? iconFilter.join(',') : String(iconFilter))
        }
        if (flagFilter !== null && flagFilter !== undefined) {
          filters.flag_filter = typeof flagFilter === 'string' ? flagFilter : (Array.isArray(flagFilter) ? flagFilter.join(',') : String(flagFilter))
        }
        if (activeWorkArea) filters.work_area = activeWorkArea
        // Contadores de icono: ADDITIVOS dentro del scope activo (cuadran con /icons/counts).
        if (counterFilter === 'ic_ge_diamond') filters.ic_quality = 'ge_diamond_plus'
        if (counterFilter === 'bulb_recent') filters.bulb_recent = '1'
      }
      // Drill-down desde Analiticas: agente y fecha directos (sin necesidad de carpeta de sistema).
      // En modo borradores o contador de accion (global) NO aplica.
      if (!draftFilter && !counterActionType) {
        if (drilldownAgent) filters.agent = drilldownAgent
        if (drilldownDate) filters.next_followup_date = drilldownDate
      }
      filters.sort = sortRef.current.map(s => `${s.column}:${s.direction}`).join(',')
      if (sortPresetRef.current) filters.sort_preset = sortPresetRef.current
      // Frozen mode: pasar include_worked_today=1 cuando frozen.active es true
      // OR cuando fullDayMode está activado (opt-in persistido). Lo segundo cubre
      // la primera carga del componente: el slice frozen rehidrata fullDayMode=true
      // desde localStorage pero active=false (defaults). Sin esta segunda condición,
      // el primer fetch trae un set sin worked_today expandido y los siguientes
      // refreshes (con frozen.active=true ya armado) traen un set distinto, causando
      // que aparezcan filas "nuevas" gradualmente.
      // INC-033: con search activo, NO pasar el flag. Search debe buscar sobre el
      // listado completo independiente de work_area, filtros y modo dia. Pasar
      // worked_today con search reactiva un workedTodayExpr en el SELECT que
      // requiere $1=user.name; combinado con el COUNT renumber expone el bug
      // INC-033 (fix backend en crm-web-api.js). Ademas la search debe ignorar
      // todo el contexto frozen/area para encontrar prospectos fuera del scope.
      const _fzn2 = useCRMStore.getState().frozen
      if (_fzn2.fullDayMode && !debouncedSearch) {
        filters.include_worked_today = 1
      }
      const data = await todolistAPI.getContacts(filters)
      const newContacts = data.contacts || data
      const newTotal = data.total || newContacts.length

      if (append) {
        setContacts(prev => {
          const updated = [...prev, ...newContacts]
          currentOffsetRef.current = updated.length
          return updated
        })
        // Extender snapshot frozen con los phones recién paginados, para que
        // futuros refreshes no los clasifiquen como "nuevos" (evita falsos positivos
        // de markNewRow/highlight verde sobre filas que solo aparecieron por scroll).
        extendFrozenSnapshot(newContacts)
        // Paginados (scroll) NO son nuevos: marcarlos vistos para que no salgan con chip NUEVO.
        if (counterFilter === 'wrote_today') wtCommitPhones(newContacts.map(c => c.phone))
      } else {
        setContacts(newContacts)
        currentOffsetRef.current = newContacts.length
        setContactsView(counterFilter)
        setSelectedIds([])
        setSelectAllFiltered(false)
        // Tomar snapshot del orden congelado al cargar la vista.
        // Si frozen no está activo: enterFrozen arranca un nuevo ciclo (deadline = +3min).
        // Si frozen ya está activo (cambio de search/sort/filtros sin descongelar):
        // refreshSnapshot reemplaza el snapshot al nuevo set sin tocar el deadline,
        // y limpia zombies/newRows del query anterior (que ya no aplican).
        if (!useCRMStore.getState().frozen.active) {
          enterFrozen(newContacts)
        } else {
          refreshSnapshot(newContacts)
        }
      }

      setTotal(newTotal)
      setTotalFiltered(newTotal)
      setHasMore(currentOffsetRef.current < newTotal)
      // Carga completa (no append/scroll): refrescar contadores en vivo (cubre bulk/día/área/reset).
      if (!append) refreshCountersRef.current?.()
    } catch (error) {
      console.error('Error cargando contactos:', error)
    } finally {
      setLoadingContacts(false)
      setLoadingMore(false)
    }
  }, [selectedFolder, selectedLabel, selectedDepartment, debouncedSearch, activeWorkArea, iconFilter, flagFilter, drilldownAgent, drilldownDate, draftFilter, counterFilter])
  const handleFolderSelect = (folder) => {
    setSelectedFolderPersist(folder)
    setSelectedContact(null) // Limpiar contacto seleccionado al cambiar lista
  }

  const handleContactSelect = (contact) => {
    setSelectedContact(contact)
    setIsPanelCollapsed(false) // Abrir panel al seleccionar contacto

    // INC-037 (2026-05-04): eliminado quick-fetch que pisaba mutaciones locales
    // recientes. El bug: clic en columna flag dispara onSelectContact (este handler),
    // que iniciaba un fetch async; usuario seleccionaba flag option (optimistic update);
    // el fetch async respondia con bandera VIEJA (porque el PUT aun no llegaba al
    // backend) y `setContacts(prev.map(c => ({...c, ...fresh})))` pisaba el bandera
    // optimistic con el viejo. Resultado: flag desaparecia "tras unos segundos".
    // El silentRefresh de cada 10s ya cubre el caso de cambios cross-agent; latencia
    // maxima 10s aceptable. ProspectPanel hace su propio fetch via /prospect/:phone
    // que carga datos detallados sin tocar la grilla.
  }

  const handleCreateFolder = async (nombre, parentId = null) => {
    try {
      await foldersAPI.createFolder(nombre, parentId)
      await loadFolders()
    } catch (error) {
      console.error('Error creando lista:', error)
    }
  }

  const handleDeleteFolder = async (folderId) => {
    try {
      await foldersAPI.deleteFolder(folderId)
      await loadFolders()
      if (selectedFolder?.id === folderId) {
        setSelectedFolderPersist(null)
      }
    } catch (error) {
      console.error('Error eliminando lista:', error)
      alert(error.response?.data?.error || 'Error eliminando lista')
    }
  }

  const handleUpdateFolder = async (folderId, updates) => {
    try {
      await foldersAPI.updateFolder(folderId, updates)
      await loadFolders()
    } catch (error) {
      console.error('Error actualizando lista:', error)
      alert(error.response?.data?.error || 'Error actualizando lista')
    }
  }

  const handleReorderFolders = async (orderedIds) => {
    try {
      await foldersAPI.reorderFolders(orderedIds)
      await loadFolders()
    } catch (error) {
      console.error('Error reordenando listas:', error)
    }
  }

  const handleMoveContact = async (contactId, targetFolderId, sourceFolderId) => {
    try {
      await foldersAPI.moveContact(targetFolderId, contactId, sourceFolderId)
      await loadContacts()
      await loadFolders() // Actualizar contadores
    } catch (error) {
      console.error('Error moviendo contacto:', error)
    }
  }

  const handleAddToFolder = async (contactId, folderId) => {
    try {
      await foldersAPI.addContactToFolder(folderId, contactId)
      await loadContacts()
      await loadFolders() // Actualizar contadores
    } catch (error) {
      console.error('Error agregando a lista:', error)
      alert(error.response?.data?.error || 'Error agregando contacto a lista')
    }
  }

  const handleRemoveFromFolder = async (contactId, folderId) => {
    try {
      await foldersAPI.removeContactFromFolder(folderId, contactId)
      await loadContacts()
      await loadFolders() // Actualizar contadores
    } catch (error) {
      console.error('Error quitando de lista:', error)
      alert(error.response?.data?.error || 'Error quitando contacto de lista')
    }
  }

  const handleSortChange = useCallback((column) => {
    // Clic en una columna SALE del preset (vuelve a orden por columnas).
    if (sortPresetRef.current) {
      sortPresetRef.current = null
      setSortPreset(null)
      saveSortPresetToStorage(user?.phone, null)
    }
    const current = sortRef.current
    const idx = current.findIndex(s => s.column === column)
    let next
    if (idx === -1) {
      // IC column prioriza ASC (rank 1 = más importante primero)
      const defaultDir = column === 'icon_current' ? 'asc' : 'desc'
      next = [...current, { column, direction: defaultDir }]
      if (next.length > 2) next = next.slice(-2)
    } else {
      // Ciclo de 3 estados: startDir -> oppositeDir -> off
      // Para icon_current: asc -> desc -> off. Para otras: desc -> asc -> off.
      const s = current[idx]
      const startDir = column === 'icon_current' ? 'asc' : 'desc'
      const oppositeDir = startDir === 'asc' ? 'desc' : 'asc'
      if (s.direction === startDir) {
        next = [...current]
        next[idx] = { column, direction: oppositeDir }
      } else {
        next = current.filter((_, i) => i !== idx)
      }
    }
    if (next.length === 0) next = DEFAULT_SORT
    sortRef.current = next
    setSortConfig(next)
    saveSortToStorage(user?.phone, next)
    // Spinner: el client-side sort en sortedContacts agrupa lo que esta cargado,
    // pero buckets fuera del set (ej: rosadas urgentes en otra pagina) recien
    // aparecen cuando llega el fetch. Mostrar spinner en el header de la columna
    // recien activada da feedback visual del trabajo en curso.
    const newPrimary = next[0]?.column || null
    setSortLoadingColumn(newPrimary)
    // Sort change SIEMPRE descongela (incluso con fullDayMode on): el agente decidio
    // reordenar y el snapshot del orden anterior ya no aplica. Tras descongelar,
    // loadContacts arranca un nuevo ciclo frozen con el orden recien aplicado.
    exitFrozen()
    loadContacts().finally(() => setSortLoadingColumn(null))
  }, [loadContacts, user?.phone, exitFrozen])

  const handleSortReset = useCallback(() => {
    sortRef.current = DEFAULT_SORT
    setSortConfig(DEFAULT_SORT)
    saveSortToStorage(user?.phone, DEFAULT_SORT)
    // Resetear orden tambien limpia el preset de "Vista".
    sortPresetRef.current = null
    setSortPreset(null)
    saveSortPresetToStorage(user?.phone, null)
    // Limpia tambien filtros de columnas (IC, Flag) — siempre van con el reset de orden
    setIconFilterPersist(null)
    setFlagFilterPersist(null)
    // Resetear orden tambien descongela (siempre, igual que handleSortChange).
    exitFrozen()
    // Resetear orden limpia ademas el drilldown activo (si habia uno desde heatmap Analiticas)
    setDrilldownAgent(null)
    setDrilldownDate(null)
    setDraftFilter(false)
    setCounterFilter(null)
    loadContacts()
  }, [loadContacts, user?.phone, setIconFilterPersist, setFlagFilterPersist, exitFrozen, draftFilter])

  // Selecciona un preset de "Vista" desde el dropdown (null = Normal/columnas).
  // Un preset es un MODO server-side excluyente con el orden por columnas: al
  // activarlo se limpia sortConfig para que el sort client-side respete el orden
  // del backend (sin badges ¹/²) y descongela (igual que handleSortChange).
  const handleSelectPreset = useCallback((preset) => {
    sortPresetRef.current = preset
    setSortPreset(preset)
    saveSortPresetToStorage(user?.phone, preset)
    sortRef.current = DEFAULT_SORT
    setSortConfig(DEFAULT_SORT)
    saveSortToStorage(user?.phone, DEFAULT_SORT)
    exitFrozen()
    loadContacts()
  }, [loadContacts, user?.phone, exitFrozen])

  // Gmail-style "Select all filtered": fetch IDs de TODO el filtro actual
  // (no solo los visibles paginados) para acciones bulk reales sobre el set completo.
  // Espeja la misma forma de filters que loadContacts: search tiene prioridad
  // sobre work_area/folder/label/dept/icon/flag.
  const handleSelectAllFiltered = useCallback(async () => {
    try {
      const allFilters = {}
      if (debouncedSearch) {
        allFilters.search = debouncedSearch
      } else if (draftFilter) {
        // Modo borradores: vista GLOBAL (igual que loadContacts) → "seleccionar todos" cuadra con la grilla.
        allFilters.draft_filter = 'has_drafts'
      } else if (counterFilter && COUNTER_ACTION_TYPES[counterFilter]) {
        // Contador de accion: vista GLOBAL del dia por agente (igual que loadContacts).
        allFilters.action_filter = COUNTER_ACTION_TYPES[counterFilter]
        if (selectedFolder && selectedFolder.es_sistema) allFilters.action_agent = selectedFolder.nombre
      } else if (counterFilter === 'wrote_today') {
        allFilters.wrote_today = '1'
        if (selectedFolder) { if (selectedFolder.es_sistema) allFilters.agent = selectedFolder.nombre; else allFilters.folder_id = selectedFolder.id }
        if (selectedLabel) allFilters.label = selectedLabel.id
        if (selectedDepartment) allFilters.department = selectedDepartment.id
      } else {
        if (selectedFolder) {
          if (selectedFolder.es_sistema) allFilters.agent = selectedFolder.nombre
          else allFilters.folder_id = selectedFolder.id
        }
        if (selectedLabel) allFilters.label = selectedLabel.id
        if (selectedDepartment) allFilters.department = selectedDepartment.id
        if (iconFilter !== null && iconFilter !== undefined) {
          allFilters.icon_filter = typeof iconFilter === 'string'
            ? iconFilter
            : (Array.isArray(iconFilter) ? iconFilter.join(',') : String(iconFilter))
        }
        if (flagFilter !== null && flagFilter !== undefined) {
          allFilters.flag_filter = typeof flagFilter === 'string'
            ? flagFilter
            : (Array.isArray(flagFilter) ? flagFilter.join(',') : String(flagFilter))
        }
        if (activeWorkArea) allFilters.work_area = activeWorkArea
        // Contadores de icono: ADDITIVOS dentro del scope activo.
        if (counterFilter === 'ic_ge_diamond') allFilters.ic_quality = 'ge_diamond_plus'
        if (counterFilter === 'bulb_recent') allFilters.bulb_recent = '1'
        // Mismo guard que loadContacts: include_worked_today solo si fullDayMode on Y sin search.
        if (useCRMStore.getState().frozen.fullDayMode) {
          allFilters.include_worked_today = 1
        }
      }
      const r = await todolistAPI.getContactIds(allFilters)
      setSelectedIds(r.ids || [])
      setSelectAllFiltered(true)
      if (r.capped) {
        console.warn('[selectAllFiltered] response capped at 5000 ids')
      }
    } catch (e) {
      console.error('[handleSelectAllFiltered]', e)
      alert('Error al obtener todos los IDs del filtro: ' + (e?.response?.data?.error || e.message))
    }
  }, [debouncedSearch, selectedFolder, selectedLabel, selectedDepartment, iconFilter, flagFilter, activeWorkArea, draftFilter, counterFilter])

  const handleClearAllFiltered = useCallback(() => {
    setSelectedIds([])
    setSelectAllFiltered(false)
  }, [])

  // Wrapper de onSelectionChange: detecta cuando el agente deseleccionó manualmente
  // una fila individual (selectedIds shrinks) estando en modo selectAllFiltered, y
  // desactiva el modo virtual (la selección ya no representa "todo el filtro").
  const handleSelectionChange = useCallback((next) => {
    setSelectedIds(next)
    if (selectAllFiltered && next.length < selectedIds.length) {
      setSelectAllFiltered(false)
    }
  }, [selectAllFiltered, selectedIds.length])

  // Click en PendingNewPill (banner verde "N nuevos · mostrar"): descongela el
  // frozen mode y recarga la grilla. Los contactos nuevos aparecerán naturalmente
  // en su posición segun el sort actual. exitFrozen ya limpia pendingNew (ver
  // store.exitFrozen). loadContacts arranca nuevo ciclo frozen con orden fresh.
  const handleShowPending = useCallback(() => {
    exitFrozen()
    loadContacts()
  }, [exitFrozen, loadContacts])

  // Optimistic assign from grid
  const handleAssignAgent = useCallback((phone, agentName) => {
    setContacts(prev => prev.map(c => c.phone === phone ? { ...c, assigned_to: agentName } : c))
    if (selectedContact && selectedContact.phone === phone) {
      setSelectedContact(prev => prev ? { ...prev, assigned_to: agentName } : prev)
    }
    ensureFrozen(contactsRef.current)
    refreshCountersRef.current?.()
  }, [selectedContact, ensureFrozen])

  // Close panel when clicking empty grid space
  const handleEmptyClick = useCallback(() => {
    setSelectedContact(null)
    setIsPanelCollapsed(true)
  }, [])

  // Optimistic flag toggle - updates UI immediately
  const handleFlagToggle = useCallback((phone, newVal) => {
    setContacts(prev => prev.map(c => c.phone === phone ? { ...c, bandera: newVal } : c))
    ensureFrozen(contactsRef.current)
    refreshCountersRef.current?.()
  }, [ensureFrozen])

  // Optimistic DIA reset
  const handleDiaReset = useCallback((phone) => {
    setContacts(prev => prev.map(c => c.phone === phone ? { ...c, no_contesta_dia: null } : c))
    ensureFrozen(contactsRef.current)
    refreshCountersRef.current?.()
  }, [ensureFrozen])

  const handleProspectUpdate = useCallback(async (phone, changes) => {
    // Actualizar solo el contacto específico en la lista (sin recargar todo)
    if (!phone || !changes) return

    // Optimistic UI: aplicar cambios al contacto en la grilla SIEMPRE, incluso si el
    // caller pidió _forceRefresh. Antes el path _forceRefresh saltaba este setContacts
    // y la grilla mostraba la fecha vieja durante búsqueda (donde el auto-refresh está
    // pausado), haciendo creer al agente que el cambio no se había guardado.
    // Bug B+C fix (2026-05-15): cualquier edicion del agente baja la manito optimistic.
    // El backend recalcula last_from_client usando MAX(notas.creada_en, pcd.updated_at,
    // fecha_asignado_icono) como baseline; editar pcd sube updated_at y la mano baja.
    // Aplicar este efecto inmediatamente evita ver la mano durante ~500ms-10s hasta el
    // siguiente silentRefresh. Campos que disparan baseline update.
    const AGENT_ACTION_FIELDS = ['nextFollowupDate', 'hint', 'no_contesta_dia', 'icon_current', 'assigned_to']
    const isAgentAction = AGENT_ACTION_FIELDS.some(f => f in changes)
    // Registrar la baja optimística para que el poll de manitos (fetchHands) no la
    // re-levante con un resultado en vuelo de antes de la gestión (guard 20s).
    if (isAgentAction) handsLoweredAtRef.current.set(phone, Date.now())
    setContacts(prev => prev.map(contact => {
      if (contact.phone !== phone) return contact
      const updates = {}
      if ('first_name' in changes || 'last_name' in changes) {
        const fn = 'first_name' in changes ? changes.first_name : (contact.name || '').split(' ')[0]
        const ln = 'last_name' in changes ? changes.last_name : ''
        updates.name = (fn + ' ' + (ln || '')).trim()
      }
      if ('nextFollowupDate' in changes) updates.next_followup_date = changes.nextFollowupDate
      if ('assigned_to' in changes) updates.assigned_to = changes.assigned_to
      if ('hint' in changes) updates.hint = changes.hint
      if ('vendido' in changes) updates.vendido = changes.vendido
      if ('ventaMonto' in changes) updates.venta_monto = changes.ventaMonto
      if ('no_contesta_dia' in changes) updates.no_contesta_dia = changes.no_contesta_dia
      // INC-036 (2026-05-04): icon_current optimistic. Antes el cambio de icono desde
      // ProspectPanel solo dispatchaba _forceRefresh; el silent refresh siguiente podia
      // no traer al contacto en fresh (cae fuera del LIMIT por su nueva prio IC) y
      // mergeIntoSnapshot mantenia los datos prev (icon viejo). Con optimistic aqui,
      // la grilla refleja el icono nuevo inmediatamente.
      if ('icon_current' in changes) updates.icon_current = changes.icon_current
      // avatar_seed NO está en AGENT_ACTION_FIELDS → no baja la manito ni toca last_from_client.
      if ('avatar_seed' in changes) updates.avatar_seed = changes.avatar_seed
      if (isAgentAction) {
        updates.last_from_client = false
        updates.has_urgent_keyword = false
        updates.has_fondo_keyword = false
      }
      return Object.keys(updates).length > 0 ? { ...contact, ...updates } : contact
    }))

    // También actualizar el contacto seleccionado si es el mismo
    if (selectedContact?.phone === phone) {
      setSelectedContact(prev => {
        if (!prev) return prev
        const updates = {}
        if ('first_name' in changes || 'last_name' in changes) {
          const fn = 'first_name' in changes ? changes.first_name : (prev.name || '').split(' ')[0]
          const ln = 'last_name' in changes ? changes.last_name : ''
          updates.name = (fn + ' ' + (ln || '')).trim()
        }
        if ('nextFollowupDate' in changes) updates.next_followup_date = changes.nextFollowupDate
        if ('assigned_to' in changes) updates.assigned_to = changes.assigned_to
        if ('hint' in changes) updates.hint = changes.hint
        if ('vendido' in changes) updates.vendido = changes.vendido
        if ('ventaMonto' in changes) updates.venta_monto = changes.ventaMonto
        if ('no_contesta_dia' in changes) updates.no_contesta_dia = changes.no_contesta_dia
        if ('icon_current' in changes) updates.icon_current = changes.icon_current
        if ('avatar_seed' in changes) updates.avatar_seed = changes.avatar_seed
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev
      })
    }

    ensureFrozen(contactsRef.current)

    // Si el caller pidió _forceRefresh (ej. NotesSection tras aplicar acciones), traer
    // datos frescos del backend para campos que el optimistic update no cubre
    // (notas count, last_from_client, etc.). El optimistic ya hizo lo principal.
    //
    // Frozen-aware (INC-034 2026-05-04): si el orden está bloqueado, usar silentRefresh
    // (aplica fresh data via applyServerUpdate sin tocar el snapshot). De lo contrario,
    // loadContacts (full reload + reset snapshot). Sin esta distincion, agregar una
    // nota mueve la fila al top porque updated_at cambia y refreshSnapshot adopta el
    // nuevo orden. force:true permite refrescar tambien en folder/label/dept (donde
    // el auto-refresh tipicamente skipea).
    if (changes._forceRefresh) {
      if (useCRMStore.getState().frozen.active) {
        await silentRefresh({ force: true })
      } else {
        await loadContacts()
      }
    }
    // Refresco en vivo de los contadores (IC/bombilla/daily) tras cualquier edición de prospecto.
    // El debounce coalesce con el posible refresh de loadContacts (force) en una sola llamada.
    refreshCounters()
  }, [selectedContact, ensureFrozen, silentRefresh, refreshCounters])

  // Limpiar selección al cambiar filtros
  const handleFolderSelectWithClear = (folder) => {
    setSelectedIds([])
    setSelectAllFiltered(false)
    handleFolderSelect(folder)
  }

  // Aplicar fecha masiva
  const handleApplyBulkDate = async () => {
    if (selectedIds.length === 0) return

    setApplyingBulkAction(true)
    try {
      const phones = selectedIds.map(id => contacts.find(c => c.id === id)?.phone).filter(Boolean)
      await todolistAPI.updateBulkDate(selectedIds, bulkDate || null)
      // Agendar masivamente = "rescatar" el prospecto: aplicar flag Rescatado automaticamente. Solo al
      // FIJAR fecha (no al limpiarla). Best-effort: si falla el flag, el agendamiento ya quedo; loadContacts() refresca.
      // EXCEPCION: Lisette hace agendamiento masivo de rutina (no es "rescate") -> NO marca Rescatado.
      const esLisette = (user?.name || '').trim().toLowerCase() === 'lisette'
      if (bulkDate && !esLisette) { try { await bulkAPI.bulkFlag(phones, 'rescatado') } catch (e) { console.warn('auto-rescatado fallo:', e) } }
      setShowBulkDateModal(false)
      setBulkDate('')
      setSelectedIds([])
      setSelectAllFiltered(false)
      await loadContacts()
    } catch (error) {
      console.error('Error aplicando fecha masiva:', error)
      alert('Error al aplicar fecha de vencimiento')
    } finally {
      setApplyingBulkAction(false)
    }
  }

  // Quitar fecha masiva
  const handleClearBulkDate = async () => {
    if (selectedIds.length === 0) return

    if (!confirm(`¿Quitar fecha de vencimiento de ${selectedIds.length} contactos?`)) return

    setApplyingBulkAction(true)
    try {
      await todolistAPI.updateBulkDate(selectedIds, null)
      setSelectedIds([])
      setSelectAllFiltered(false)
      await loadContacts()
    } catch (error) {
      console.error('Error quitando fecha masiva:', error)
      alert('Error al quitar fecha de vencimiento')
    } finally {
      setApplyingBulkAction(false)
    }
  }

  // Cambiar icono masivamente
  const handleBulkIcon = async (iconId) => {
    console.log('[BulkIcon] iconId:', iconId, 'selectedIds:', selectedIds.length)
    if (selectedIds.length === 0 || !iconId) { console.warn('[BulkIcon] Aborted: no selectedIds or no iconId'); return }
    setApplyingBulkAction(true)
    try {
      const phones = selectedIds.map(id => contacts.find(c => c.id === id)?.phone).filter(Boolean)
      await bulkAPI.updateIcon(phones, iconId)
      setShowBulkIconModal(false)
      setSelectedIds([])
      setSelectAllFiltered(false)
      loadContacts()
    } catch (err) {
      console.error('Bulk icon error:', err)
    } finally {
      setApplyingBulkAction(false)
    }
  }

  // Agregar a carpeta masivamente
  const handleBulkAssign = async (agentName, agentId) => {
    if (selectedIds.length === 0) return
    try {
      const phones = selectedIds.map(id => contacts.find(c => c.id === id)?.phone).filter(Boolean)
      await bulkAPI.assign(phones, agentId || null, agentName || null)
      setContacts(prev => prev.map(c =>
        phones.includes(c.phone) ? { ...c, assigned_to: agentName } : c
      ))
      setShowBulkAssignModal(false)
      setSelectedIds([])
      setSelectAllFiltered(false)
    } catch (err) {
      console.error('Bulk assign failed:', err)
      alert('Error al asignar: ' + (err.response?.data?.error || err.message))
    }
  }

  const handleBulkAddToFolder = async (folderId) => {
    if (selectedIds.length === 0 || !folderId) return

    setApplyingBulkAction(true)
    try {
      await todolistAPI.bulkAddToFolder(selectedIds, folderId)
      setShowBulkFolderModal(false)
      setSelectedIds([])
      setSelectAllFiltered(false)
      await loadContacts()
      await loadFolders()
    } catch (error) {
      console.error('Error agregando a lista:', error)
      alert('Error al agregar contactos a lista')
    } finally {
      setApplyingBulkAction(false)
    }
  }

  // En frozen mode, el orden lo controla el snapshot del store; los indicadores
  // (dots, zombies, new) se aplican via data-attrs en ContactsTable. Aquí
  // simplemente exponemos los contacts tal cual.
  const displayContacts = contacts

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full dark:bg-surface-dark">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-1 overflow-hidden dark:bg-surface-dark">
      {/* Panel izquierdo: Árbol de listas y filtros - Auto-collapse */}
      <div
        className={`
          flex-shrink-0 border-r border-gray-200 dark:border-surface-dark-border overflow-hidden relative
          transition-all duration-300 ease-in-out
          ${isFilterExpanded ? 'w-72 shadow-lg' : 'w-12'}
        `}
        onMouseEnter={handleFilterEnter}
        onMouseLeave={handleFilterLeave}
        onClick={!isFilterExpanded ? handleFilterClick : undefined}
      >
        {/* === Estado colapsado: iconos minimalistas === */}
        <div className={`
          absolute inset-y-0 left-0 w-12 flex flex-col items-center py-4 gap-3 bg-white dark:bg-slate-900 border-r border-gray-100 dark:border-slate-700 z-10
          transition-opacity duration-200 cursor-pointer
          ${isFilterExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100'}
        `}>
          {/* Icono principal de filtros */}
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </div>

          {/* Indicador de carpeta seleccionada */}
          {selectedFolder && (
            <div className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center" title={selectedFolder.nombre}>
              <span className="text-sm">{selectedFolder.icono || '📋'}</span>
            </div>
          )}

          {/* Indicador de etiqueta activa */}
          {selectedLabel && (
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: selectedLabel.color || '#6B7280' }}
              title={selectedLabel.name}
            >
              <span className="text-[10px]">🏷️</span>
            </div>
          )}

          {/* Indicador de departamento activo */}
          {selectedDepartment && (
            <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center" title={selectedDepartment.name}>
              <span className="text-sm">🏢</span>
            </div>
          )}
        </div>

        {/* === Estado expandido: contenido completo === */}
        <div className={`
          w-72 h-full flex flex-col bg-gray-100 dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700
          transition-opacity duration-300
          ${isFilterExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}>
          <div className="p-4 border-b border-gray-200 dark:border-surface-dark-border bg-white dark:bg-surface-dark-alt">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-md">
                <span className="text-white text-lg">📋</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filtros</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Organiza tus contactos</p>
              </div>
            </div>
          </div>
          <FolderTree
            folders={folders}
            selectedFolder={selectedFolder}
            onSelectFolder={handleFolderSelectWithClear}
            onCreateFolder={handleCreateFolder}
            onUpdateFolder={handleUpdateFolder}
            onDeleteFolder={handleDeleteFolder}
            onDropContact={handleMoveContact}
            onReorderFolders={handleReorderFolders}
            labels={labels}
            departments={departments}
            selectedLabel={selectedLabel}
            selectedDepartment={selectedDepartment}
            onSelectLabel={setSelectedLabelPersist}
            onSelectDepartment={setSelectedDepartmentPersist}
          />
        </div>
      </div>

      {/* Panel central: Tabla de contactos - Estilo Koomo */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-surface-dark transition-all duration-300">
        <div className="p-4 border-b border-gray-200 dark:border-surface-dark-border bg-white dark:bg-surface-dark-alt shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {/* Icono/Emoji de la lista */}
              {selectedFolder ? (
                <span className="text-2xl">{selectedFolder.icono || '📋'}</span>
              ) : (
                <span className="text-2xl">📋</span>
              )}
              <div
                className="w-1.5 h-8 rounded-full"
                style={{ backgroundColor: selectedFolder?.color || '#6366F1' }}
              />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {selectedFolder ? selectedFolder.nombre : 'Todos los contactos'}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  <span className="font-semibold text-gray-700 dark:text-gray-200 inline-block text-right tabular-nums" style={{ minWidth: '3ch' }}>
                    {contacts.length < total ? `${contacts.length}/${total}` : contacts.length}
                  </span>
                  {' '}contacto{contacts.length !== 1 ? 's' : ''}
                  {contacts.length < total && <span className="ml-1 text-primary-600 dark:text-primary-400 whitespace-nowrap" title="Scroll para cargar más">↓ más</span>}
                  {selectedFolder?.overdue_count > 0 && (
                    <span className="ml-1.5 text-red-600 whitespace-nowrap">· {selectedFolder.overdue_count} venc.</span>
                  )}
                </p>
              </div>

              {/* Botones de edición para listas personalizadas */}
              {selectedFolder && !selectedFolder.es_sistema && (
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => {
                      const nuevoNombre = prompt('Nuevo nombre de la lista:', selectedFolder.nombre)
                      if (nuevoNombre && nuevoNombre.trim() && nuevoNombre !== selectedFolder.nombre) {
                        handleUpdateFolder(selectedFolder.id, { nombre: nuevoNombre.trim() })
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition"
                    title="Editar nombre"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => {
                      handleUpdateFolder(selectedFolder.id, { archivada: !selectedFolder.archivada })
                    }}
                    className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                    title={selectedFolder.archivada ? 'Desarchivar lista' : 'Archivar lista'}
                  >
                    {selectedFolder.archivada ? '📤' : '📥'}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`¿Eliminar la lista "${selectedFolder.nombre}"?\nLos contactos no se eliminarán, solo se quitarán de esta lista.`)) {
                        handleDeleteFolder(selectedFolder.id)
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Eliminar lista"
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>

            {/* Campo de búsqueda - Estilo Koomo (achicado para dar espacio a los contadores fijos) */}
            <div className="flex-1 min-w-0 max-w-xs relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar nombre, teléfono, agente, etiqueta, campaña..."
                className="w-full px-4 py-2.5 pl-10 border border-gray-200 dark:border-surface-dark-border rounded-xl text-base bg-gray-50 dark:bg-surface-dark dark:text-gray-100 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-surface-dark-alt focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition shadow-sm"
              />
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-surface-dark-hover rounded-full transition"
                >
                  ×
                </button>
              )}
            </div>

            {/* Contadores fijos (siempre visibles): Sí Contesta · Si WSP · No Contesta · Notas pendientes · IC≥Diamante Info+ · Bombillas (forma de bombilla). items-start alinea los cuerpos (la bombilla es más alta por el soquete). */}
            <div className="flex items-start gap-1.5 flex-shrink-0">
              <FixedCounter value={dailyStats.si_contesta} bg="bg-green-500" textColor="white" label="Sí Contesta hoy" onClick={() => handleToggleCounterFilter('act_si_contesta')} active={counterFilter === 'act_si_contesta'} />
              <FixedCounter value={dailyStats.si_whatsapp} bg="bg-emerald-200" textColor="white" label="Sí WhatsApp hoy" onClick={() => handleToggleCounterFilter('act_si_whatsapp')} active={counterFilter === 'act_si_whatsapp'} />
              <FixedCounter value={dailyStats.no_contesta} bg="bg-red-500" textColor="white" label="No Contesta hoy" onClick={() => handleToggleCounterFilter('act_no_contesta')} active={counterFilter === 'act_no_contesta'} />
              <FixedCounter value={dailyStats.drafts_pending} bg="bg-orange-400" textColor="dark" label="Notas pendientes (borradores)" onClick={handleToggleDraftFilter} active={draftFilter} />
              <FixedCounter value={icCount} bg="bg-sky-500" textColor="white" label="Prospectos con IC ≥ Diamante Info+ en el área activa" onClick={() => handleToggleCounterFilter('ic_ge_diamond')} active={counterFilter === 'ic_ge_diamond'} />
              <BulbCounter value={bulbCount} label="Bombillas encendidas creadas hace ≤2 días en el área activa" onClick={() => handleToggleCounterFilter('bulb_recent')} active={counterFilter === 'bulb_recent'} />
              <FixedCounter value={wroteTodayCount} bg="bg-fuchsia-500" textColor="white" label="Escribieron hoy (o ayer 18:00) y NO agendados hoy" onClick={() => handleToggleCounterFilter('wrote_today')} active={counterFilter === 'wrote_today'} pulse={hasNewWriters} />
            </div>

            {/* Botón nuevo contacto */}
            {(searchTerm || selectedFolder || selectedLabel || selectedDepartment || activeWorkArea || iconFilter !== null || flagFilter !== null || draftFilter || counterFilter || sortPreset || !isDefaultSortConfig(sortConfig)) && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedFolderPersist(null)
                  setSelectedLabelPersist(null)
                  setSelectedDepartmentPersist(null)
                  setActiveWorkAreaPersist(null)
                  setIconFilterPersist(null)
                  setFlagFilterPersist(null)
                  handleSortReset()
                  setSelectedContact(null)
                  setIsPanelCollapsed(true)
                  // Si fullDayMode esta on, mantener el orden congelado (agente puede
                  // descongelar manualmente con boton del chip o desactivando el toggle).
                  // Sin fullDayMode, comportamiento original: reset descongela.
                  exitFrozenIfNotFullDay()
                }}
                className="flex-shrink-0 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 rounded-xl text-sm hover:bg-red-100 dark:hover:bg-red-900/40 transition font-medium"
                title="Limpiar todos los filtros"
              >
                Reset
              </button>
            )}

            <button
              onClick={() => setShowAddContactModal(true)}
              className="flex-shrink-0 px-3 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl text-sm hover:from-primary-700 hover:to-primary-800 transition font-medium shadow-sm flex items-center gap-1.5"
            >
              <span>+</span>
              <span>Nuevo</span>
            </button>

            {/* Column selector */}
            <ColumnSelector visible={visibleColumns} onChange={handleVisibilityChange} />

            {/* Badges de filtros activos - Estilo Koomo */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {selectedLabel && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium text-white shadow-sm"
                  style={{ backgroundColor: selectedLabel.color || '#6B7280' }}
                >
                  🏷️ {selectedLabel.name}
                  <button
                    onClick={() => setSelectedLabelPersist(null)}
                    className="ml-0.5 w-4 h-4 flex items-center justify-center hover:bg-white/20 rounded-full transition"
                  >×</button>
                </span>
              )}
              {selectedDepartment && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium bg-purple-100 text-purple-700 shadow-sm">
                  🏢 {selectedDepartment.name}
                  <button
                    onClick={() => setSelectedDepartmentPersist(null)}
                    className="ml-0.5 w-4 h-4 flex items-center justify-center hover:bg-purple-200 rounded-full transition"
                  >×</button>
                </span>
              )}
            </div>

            {/* Reabrir panel: espejo del boton "Cerrar panel" (gris->azul, chevron ◀), pegado al borde + pulso suave */}
            {selectedContact && isPanelCollapsed && (
              <button
                onClick={() => setIsPanelCollapsed(false)}
                className="flex-shrink-0 -mr-2 py-3 px-2.5 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-slate-700 dark:to-slate-800 text-gray-500 dark:text-gray-400 hover:from-primary-500 hover:to-primary-600 dark:hover:from-primary-500 dark:hover:to-primary-600 hover:text-white dark:hover:text-white hover:scale-110 hover:shadow-lg hover:shadow-primary-400/50 hover:ring-2 hover:ring-primary-300/60 transition-all duration-200 group animate-panel-pulse hover:animate-none"
                title="Abrir panel de prospecto"
              >
                <svg className="w-4 h-4 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Work Area Pills - Date Filters */}
        <div className="px-4 py-2 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700">
          <WorkAreaPills
            counts={workAreaCounts}
            activeArea={activeWorkArea}
            onAreaChange={setActiveWorkAreaPersist}
            filterActive={!!(counterFilter || draftFilter)}
            agentPhone={user?.phone || 'default'}
          />
        </div>

        {/* Barra de acciones masivas - Altura fija para evitar desplazamiento */}
        <div className={`h-12 px-4 flex items-center justify-between border-b dark:border-surface-dark-border transition-all duration-200 ${
          selectedIds.length > 0
            ? 'bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-900/20 border-primary-200 dark:border-primary-800'
            : 'bg-gray-50 dark:bg-surface-dark border-gray-100 dark:border-surface-dark-border'
        }`}>
          {/* Lado izquierdo: reset de orden (si hay sort activo) + info de seleccion */}
          <div className="flex items-center gap-3">
            <PendingNewPill onClick={handleShowPending} />
            {selectedIds.length === 0 && <FrozenStatusChip />}
            {selectedIds.length === 0 && <SortPresetDropdown value={sortPreset} onChange={handleSelectPreset} />}
            {(sortConfig.length > 0 || sortPreset || iconFilter !== null || flagFilter !== null || drilldownAgent || drilldownDate || draftFilter || counterFilter) && (
              <button
                onClick={handleSortReset}
                className="px-2.5 py-1 bg-white dark:bg-surface-dark-alt border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-semibold hover:bg-purple-50 dark:hover:bg-surface-dark-hover transition shadow-sm flex items-center gap-1"
                title="Resetear ordenamiento y filtros (IC, Flag, drill-down de agendamientos)"
              >
                <span>↺</span>
              </button>
            )}
            {selectedIds.length > 0 && (
              <>
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {selectedIds.length} seleccionado{selectedIds.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => { setSelectedIds([]); setSelectAllFiltered(false) }}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 underline"
                >
                  Limpiar
                </button>
              </>
            )}
          </div>
          {/* Lado derecho: botones bulk si hay seleccion, o placeholder si no hay nada */}
          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end overflow-x-auto">
            {selectedIds.length > 0 ? (
              <>
                <button
                  onClick={() => setShowBulkFolderModal(true)}
                  title="Agregar a lista"
                  disabled={applyingBulkAction}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 rounded-lg text-sm hover:bg-primary-50 dark:hover:bg-surface-dark-hover transition disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                >
                  <span>📋</span>
                </button>
                <button
                  onClick={() => setShowSmartReschedule(true)}
                  disabled={applyingBulkAction || selectedIds.length === 0}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 rounded-lg text-sm hover:bg-emerald-50 dark:hover:bg-surface-dark-hover transition disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                  title="Agendamiento inteligente: reagenda por calidad en los proximos dias habiles"
                >
                  <span>&#10024;</span>
                </button>
                <button
                  onClick={() => setShowBulkDateModal(true)}
                  title="Fijar fecha de vencimiento"
                  disabled={applyingBulkAction}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 rounded-lg text-sm hover:bg-primary-50 dark:hover:bg-surface-dark-hover transition disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                >
                  <span>📅</span>
                </button>
                <button
                  onClick={() => setShowBulkIconModal(true)}
                  title="Asignar icono"
                  disabled={applyingBulkAction}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 rounded-lg text-sm hover:bg-purple-50 dark:hover:bg-surface-dark-hover transition disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                >
                  <span>&#11088;</span>
                </button>
                <button
                  onClick={() => setShowBulkAssignModal(true)}
                  title="Asignar a agente"
                  disabled={applyingBulkAction}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded-lg text-sm hover:bg-blue-50 dark:hover:bg-surface-dark-hover transition disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                >
                  <span>&#128100;</span>
                </button>
                <button
                  onClick={() => setShowBulkFlagModal(true)}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-md flex items-center gap-1.5 shadow-sm text-amber-700 dark:text-amber-300"
                  title="Aplicar bandera a contactos seleccionados"
                >
                  <span className="text-base leading-none">🚩</span>
                </button>
                <button
                  onClick={() => setShowAddToCampaignModal(true)}
                  disabled={selectedIds.length === 0}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 shadow-md shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 hover:-translate-y-0.5 transition-all duration-200 animate-pulse-once disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap"
                  title="Agregar prospectos seleccionados a una campaña"
                >
                  <span>&#128227;</span>
                </button>
                <button
                  onClick={handleClearBulkDate}
                  disabled={applyingBulkAction}
                  className="px-3 py-1.5 bg-white dark:bg-surface-dark-alt border border-gray-300 dark:border-surface-dark-border text-gray-600 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-surface-dark-hover transition disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
                  title="Quitar fecha de vencimiento"
                >
                  <span className="inline-flex items-baseline"><span>📅</span><span className="ml-0.5 text-[11px] font-bold text-red-500">✕</span></span>
                </button>
              </>
            ) : sortConfig.length === 0 ? (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Selecciona contactos para ver acciones masivas
              </span>
            ) : null}
          </div>
        </div>

        <SelectAllFilteredBanner
          visibleCount={contacts.length}
          totalCount={totalFiltered}
          selectedCount={selectedIds.length}
          selectAllFiltered={selectAllFiltered}
          onSelectAll={handleSelectAllFiltered}
          onClearAll={handleClearAllFiltered}
        />

        <ContactsTable
          contacts={displayContacts}
          newWrotePhones={counterFilter === 'wrote_today' ? newWrotePhones : undefined}
          loading={loadingContacts}
          selectedContact={selectedContact}
          onSelectContact={handleContactSelect}
          currentFolderId={selectedFolder?.es_sistema ? null : selectedFolder?.id}
          folders={folders}
          onAddToFolder={handleAddToFolder}
          onRemoveFromFolder={handleRemoveFromFolder}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={() => loadContacts(true)}
          scrollRef={tableScrollRef}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
          sortLoadingColumn={sortLoadingColumn}
                onFlagToggle={handleFlagToggle}
                onDiaReset={handleDiaReset}
                onInlineMutation={(phone, val) => {
                  // Optimistic: cualquier edicion del agente baja la mano (last_from_client=false)
                  // porque el backend recalcula el baseline a updated_at de pcd y la mano deja
                  // de aplicar. Bug B (2026-05-15): antes solo extendia el frozen deadline,
                  // dejaba la mano visible hasta auto-refresh 10s o reload manual.
                  // Guard manitos-en-vivo: que el poll fetchHands no re-levante esta baja (20s).
                  handsLoweredAtRef.current.set(phone, Date.now())
                  setContacts(prev => prev.map(c => {
                    if (c.phone !== phone) return c
                    const updates = { last_from_client: false, has_urgent_keyword: false, has_fondo_keyword: false }
                    if (val !== undefined) updates.hint = val
                    return { ...c, ...updates }
                  }))
                  ensureFrozen(contactsRef.current)
                  // Force silentRefresh para sincronizar campos calculados por backend
                  // (icon_priority no cambia pero updated_at si, hint quiza tambien tsv).
                  if (useCRMStore.getState().frozen.active) {
                    silentRefresh({ force: true })
                  }
                  refreshCountersRef.current?.()
                }}
                onBulkIcon={handleBulkIcon}
          onAssignAgent={handleAssignAgent}
          onEmptyClick={handleEmptyClick}
          iconFilter={iconFilter}
          onIconFilterChange={setIconFilterPersist}
          flagFilter={flagFilter}
          onFlagFilterChange={setFlagFilterPersist}
          total={total}
          visibleColumns={visibleColumns}
          columnWidths={columnWidths}
          onColumnWidthChange={handleWidthChange}
          onIconChanged={(phone, newIconName) => {
            if (newIconName) {
              setContacts(prev => prev.map(c => c.phone === phone ? { ...c, icon_current: newIconName } : c))
            }
            ensureFrozen(contactsRef.current)
            refreshCountersRef.current?.()
          }}
          draftPhones={draftPhones}
          onDiaClick={(contact) => setDayPickerContact(contact)}
          scopeFilters={{
            work_area: activeWorkArea,
            // System folder (es_sistema=true) representa un agente: filtra por wassenger_asignado_a.
            // User folder (custom): filtra por prospect_folder_contacts.folder_id.
            // Mismo split que en loadContacts (lineas 583-588).
            folder_id: (selectedFolder && !selectedFolder.es_sistema) ? selectedFolder.id : undefined,
            agent: (selectedFolder && selectedFolder.es_sistema) ? selectedFolder.nombre : undefined,
            label: selectedLabel?.id,
            department: selectedDepartment?.id,
            flag_filter: flagFilter,
          }}
        />
      </div>

      {/* Panel derecho: Detalles del prospecto - Estilo Koomo con animación */}
      <div
        className={`
          flex-shrink-0 bg-white dark:bg-surface-dark-alt flex flex-col overflow-hidden
          transition-all duration-300 ease-out
          ${selectedContact && !isPanelCollapsed
            ? 'w-[30%] border-l border-gray-200 dark:border-surface-dark-border shadow-xl'
            : 'w-0'
          }
        `}
      >
        {selectedContact && (
          <ProspectPanel
            contact={selectedContact}
            onClose={() => setIsPanelCollapsed(true)}
            onUpdate={handleProspectUpdate}
            onDraftChanged={refreshDraftPhones}
            mode="todolist"
            onIcPendingEnqueue={enqueueAfterNoteSave}
          />
        )}
      </div>

      {/* (boton de reabrir panel movido al toolbar, junto a Columnas) */}

      {/* Modal para aplicar fecha masiva */}
      {showBulkDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBulkDateModal(false)} />
          <div className="relative bg-white dark:bg-surface-dark-alt rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">Aplicar Fecha de Vencimiento</h3>
              <p className="text-primary-100 text-sm">A {selectedIds.length} contacto{selectedIds.length !== 1 ? 's' : ''} seleccionado{selectedIds.length !== 1 ? 's' : ''}</p>
            </div>

            {/* Body */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de vencimiento
              </label>
              <input
                type="date"
                value={bulkDate}
                onChange={(e) => setBulkDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-surface-dark-border rounded-xl dark:bg-surface-dark dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
                autoFocus
              />
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
                    setBulkDate(hoy)
                  }}
                  className="px-3 py-1.5 text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-700 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 transition"
                >
                  HOY
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const _p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-')
                    const d = new Date(+_p[0], +_p[1] - 1, +_p[2])
                    d.setDate(d.getDate() + 1)
                    const yyyy = d.getFullYear()
                    const mm = String(d.getMonth() + 1).padStart(2, '0')
                    const dd = String(d.getDate()).padStart(2, '0')
                    setBulkDate(`${yyyy}-${mm}-${dd}`)
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-surface-dark dark:text-gray-300 border border-gray-200 dark:border-surface-dark-border rounded-lg hover:bg-gray-200 dark:hover:bg-surface-dark-hover transition"
                >
                  Mañana
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const _p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-')
                    const d = new Date(+_p[0], +_p[1] - 1, +_p[2])
                    d.setDate(d.getDate() + 7)
                    const yyyy = d.getFullYear()
                    const mm = String(d.getMonth() + 1).padStart(2, '0')
                    const dd = String(d.getDate()).padStart(2, '0')
                    setBulkDate(`${yyyy}-${mm}-${dd}`)
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-surface-dark dark:text-gray-300 border border-gray-200 dark:border-surface-dark-border rounded-lg hover:bg-gray-200 dark:hover:bg-surface-dark-hover transition"
                >
                  +7 días
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const _p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-')
                    const d = new Date(+_p[0], +_p[1] - 1, +_p[2])
                    d.setMonth(d.getMonth() + 1)
                    const yyyy = d.getFullYear()
                    const mm = String(d.getMonth() + 1).padStart(2, '0')
                    const dd = String(d.getDate()).padStart(2, '0')
                    setBulkDate(`${yyyy}-${mm}-${dd}`)
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-surface-dark dark:text-gray-300 border border-gray-200 dark:border-surface-dark-border rounded-lg hover:bg-gray-200 dark:hover:bg-surface-dark-hover transition"
                >
                  +1 mes
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkDateModal(false)
                  setBulkDate('')
                }}
                className="px-5 py-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-xl transition font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleApplyBulkDate}
                disabled={!bulkDate || applyingBulkAction}
                className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition disabled:opacity-50 font-medium shadow-md"
              >
                {applyingBulkAction ? 'Aplicando...' : 'Aplicar Fecha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear nuevo contacto */}
      {showAddContactModal && (
        <AddContactModal
          onClose={() => setShowAddContactModal(false)}
          onCreated={() => { loadContacts(); loadFolders() }}
          onNavigateToContact={(phone) => {
            // Escribir el teléfono en el campo de búsqueda para que el backend lo encuentre
            const digits = (phone || '').replace(/[^\d]/g, '')
            setSearchTerm(digits)
          }}
          folders={folders}
        />
      )}

      {/* Modal para agregar a lista masivamente */}
      {showBulkFolderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBulkFolderModal(false)} />
          <div className="relative bg-white dark:bg-surface-dark-alt rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">Agregar a Lista</h3>
              <p className="text-primary-100 text-sm">{selectedIds.length} contacto{selectedIds.length !== 1 ? 's' : ''} seleccionado{selectedIds.length !== 1 ? 's' : ''}</p>
            </div>

            {/* Body - Listas disponibles */}
            <div className="p-4 max-h-80 overflow-y-auto">
              {folders.filter(f => !f.es_sistema && !f.archivada).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-3xl block mb-2">📋</span>
                  <p className="dark:text-gray-300">No hay listas personalizadas</p>
                  <p className="text-xs text-gray-400 mt-1">Crea una lista desde el panel izquierdo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {folders.filter(f => !f.es_sistema && !f.archivada).map(folder => (
                    <button
                      key={folder.id}
                      onClick={() => handleBulkAddToFolder(folder.id)}
                      disabled={applyingBulkAction}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-surface-dark hover:bg-primary-50 dark:hover:bg-surface-dark-hover rounded-xl transition text-left disabled:opacity-50 group"
                    >
                      <span className="text-lg">{folder.icono || '📋'}</span>
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: folder.color || '#6B7280' }}
                      />
                      <span className="flex-1 font-medium text-gray-700 dark:text-gray-200 group-hover:text-primary-700 dark:group-hover:text-primary-300">
                        {folder.nombre}
                      </span>
                      <span className="text-xs text-gray-400 bg-gray-200 dark:bg-surface-dark-hover dark:text-gray-500 px-2 py-0.5 rounded-full">
                        {folder.contact_count || 0}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={async () => {
                  const n = prompt('Nombre de la nueva lista:');
                  if (n && n.trim()) {
                    await handleCreateFolder(n.trim());
                  }
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary-50 dark:hover:bg-surface-dark-hover rounded-xl transition text-left group border-t border-gray-200 dark:border-surface-dark-border"
              >
                <span className="text-lg text-primary-500">+</span>
                <span className="flex-1 font-medium text-primary-600 dark:text-primary-400">Crear nueva lista</span>
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-surface-dark-border flex justify-end">
              <button
                onClick={() => setShowBulkFolderModal(false)}
                className="px-5 py-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-xl transition font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para cambiar icono masivamente */}
      {showBulkIconModal && (
        <IconSelectorModal
          phone={null}
          currentIconId={null}
          onClose={() => setShowBulkIconModal(false)}
          onIconChanged={(iconId) => handleBulkIcon(iconId)}
        />
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBulkAssignModal(false)} />
          <div className="relative bg-white dark:bg-surface-dark-alt rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">Asignar Agente</h3>
              <p className="text-blue-100 text-sm">A {selectedIds.length} contacto{selectedIds.length !== 1 ? 's' : ''} seleccionado{selectedIds.length !== 1 ? 's' : ''}</p>
            </div>
            <BulkAgentPicker onPick={handleBulkAssign} onCancel={() => setShowBulkAssignModal(false)} />
          </div>
        </div>
      )}

      {/* Bulk Flag Modal */}
      {showSmartReschedule && (
        <SmartRescheduleModal
          contactIds={selectedIds}
          isAdmin={user?.is_admin === true}
          onClose={(removedIds) => {
            setShowSmartReschedule(false)
            if (Array.isArray(removedIds) && removedIds.length) {
              setSelectedIds(prev => prev.filter(id => !removedIds.includes(id)))
              setSelectAllFiltered(false)
            }
          }}
          onApplied={() => loadContacts()}
        />
      )}
      <BulkFlagSelectorModal
        open={showBulkFlagModal}
        onClose={() => setShowBulkFlagModal(false)}
        count={selectedIds.length}
        onConfirm={async (bandera) => {
          try {
            const phones = contacts.filter(c => selectedIds.includes(c.id)).map(c => c.phone)
            await bulkAPI.bulkFlag(phones, bandera)
            // Optimistic UI
            setContacts(prev => prev.map(c =>
              selectedIds.includes(c.id) ? { ...c, bandera } : c
            ))
            setShowBulkFlagModal(false)
            setSelectedIds([])
          } catch (e) {
            console.error('[BulkFlag] failed:', e)
            alert('Error al aplicar bandera')
          }
        }}
      />

      {/* IC Confirmation Modal: aparece tras guardar nota para confirmar icono */}
      {currentPending && (
        <ICConfirmationModal
          pending={currentPending}
          currentIndex={currentIndex}
          totalCount={totalCount}
          onConfirm={async (icon) => {
            const phone = currentPending?.phone
            await confirmCurrent(icon)
            // Sync optimistic: contacts array + selectedContact panel (mismo patrón que onIconChanged)
            if (phone) {
              setContacts(prev => prev.map(c => c.phone === phone ? { ...c, icon_current: icon } : c))
              setSelectedContact(prev => prev?.phone === phone ? { ...prev, icon_current: icon } : prev)
              ensureFrozen(contactsRef.current)
            }
          }}
          loading={icLoading}
        />
      )}

      {/* Bulk Add to Campaign Modal */}
      {showAddToCampaignModal && (
        <BulkAddToCampaignPicker
          contactIds={selectedIds}
          onClose={() => setShowAddToCampaignModal(false)}
          onSuccess={(result) => {
            setShowAddToCampaignModal(false);
            alert(`✅ ${result.enrolled} prospectos inscritos`);
            // Optimista: el backend pone bandera='campana' solo si estaba NULL
            // (preserva flags manuales). Reflejar al instante en la grilla, igual
            // que el patron de bulk-flag, para que el flag de campaña se vea sin
            // refrescar manualmente.
            const enrolledIds = selectedIds;
            setContacts(prev => prev.map(c =>
              // Espeja la regla del backend: solo bandera IS NULL → 'campana'
              // (preserva flags manuales). silentRefresh reconcilia lo demas.
              enrolledIds.includes(c.id) && c.bandera == null ? { ...c, bandera: 'campana' } : c
            ));
            ensureFrozen(contactsRef.current);
            setSelectedIds([]);
            setSelectAllFiltered(false);
            // Reconciliacion autoritativa con backend (frozen-aware, preserva orden/scroll)
            silentRefresh({ force: true });
          }}
        />
      )}

      {/* Day Picker Modal: edicion rapida de DIA desde celda de grilla */}
      <DayPickerModal
        open={!!dayPickerContact}
        onClose={() => setDayPickerContact(null)}
        source="day_picker"
        currentDay={dayPickerContact?.no_contesta_dia}
        asDate=""
        setAsDate={() => {}}
        asTime=""
        setAsTime={() => {}}
        lockDate={true}
        onConfirm={async (day) => {
          if (!dayPickerContact) return
          const phone = dayPickerContact.phone
          try {
            const { prospectAPI } = await import('../../services/api')
            await prospectAPI.updateProspect(phone, { no_contesta_dia: day })
            if (typeof handleProspectUpdate === 'function') {
              handleProspectUpdate(phone, { no_contesta_dia: day })
            }
          } catch (e) {
            console.error('[DayPicker] update failed:', e)
          } finally {
            setDayPickerContact(null)
          }
        }}
      />
    </div>
  )
}
